/**
 * API Module - Centralized Export Point
 * 
 * This module exports:
 * 1. NEW: Centralized API services (congregation, user, events, donations, quotes)
 * 2. NEW: Core HTTP client with interceptors and error handling
 * 3. LEGACY: Old API functions (for backward compatibility during migration)
 * 
 * MIGRATION GUIDE:
 * - Import new services: import { congregationService, userService } from '@/api'
 * - Import new errors: import { ApiError, isApiError } from '@/api'
 * - OLD functions are deprecated, migrate to new services
 */

// ============================================================================
// SECTION 1: NEW CENTRALIZED API SERVICES (PREFERRED)
// ============================================================================

// Core HTTP client and error handling
export { ApiClient } from './core/api-client';
export { HttpClient } from './core/http-client';
export {
  ApiError,
  isApiError,
  NetworkError,
  TimeoutError,
} from './core/errors';

// Service instances - use these throughout the app
export { congregationService } from './services/congregation';
export { quoteService } from './services/quotes';
export { userService } from './services/user';
export { calendarService } from './services/calendar';
export { eventService } from './services/events';
export { donationService } from './services/donations';
export { articlesService } from './services/articles';
export { leaderboardService } from './services/leaderboard';
export { subscriptionsService } from './services/subscriptions';
export { campaignsService } from './services/campaigns';
export { darshanService } from './services/darshan';
export { sadhanaService } from './services/sadhana';

// Service types
export type { CongregationData, CongregationMember } from './services/congregation';
export type { Quote } from './services/quotes';
export type { User } from './services/user';
export type { Event } from './services/events';
export type { Donation } from './services/donations';
export type { Article } from './services/articles';
export type { LeaderboardEntry, LeaderboardData } from './services/leaderboard';
export type {
  SubscriptionPlan,
  Subscription,
  VerificationPayload,
  SubscriptionDonation,
} from './services/subscriptions';
export type { Campaign, CampaignCreatePayload } from './services/campaigns';
export type { DarshanMedia, DarshanType, DarshanParams } from './services/darshan';

// Sadhana types (Phase 2)
export type {
  WeeklySadhanaCard,
  WeeklySadhanaCardSummary,
  WeeklySadhanaEntry,
  WeeklySadhanaEntryInput,
  WeeklySadhanaCardsResponse,
  SubmitWeeklySadhanaRequest,
  SubmitWeeklySadhanaResponse,
  MentorCommentRequest,
  MentorCommentResponse,
  SadhanaApiError,
  DayOfWeek,
} from '../types/sadhana';
export { getDayName, calculateSevaTotalMinutes, getPointLabel } from '../types/sadhana';

// ============================================================================
// SECTION 2: LEGACY API FUNCTIONS (DEPRECATED - FOR BACKWARD COMPATIBILITY)
// ============================================================================

import { ApiError } from './errors';
import type { User, Article, QuoteOfTheDay, Announcement, Event } from '../models/all';
import type {
  CongregationApplication,
  CongregationApplicationApi,
  CongregationApplicationsApiResponse,
  CongregationApplicationsResponse,
  CongregationApplicationSubmitPayload,
  CongregationApplicationSubmitRequest,
  CongregationMeResponse,
  CongregationMastersPayload,
  CongregationMentorApplicationsResponse,
  CongregationMentorDecisionInput,
  CongregationMentorRecommendationInput,
  CongregationMentorDevoteeCreatePayload,
  CongregationProfileSnapshot,
  CongregationMemberPersonalInput,
  CongregationMember,
  CongregationContactPoint,
  CongregationContactInput,
  CongregationContactsResponse,
  CongregationAddress,
  CongregationAddressInput,
  CongregationAddressesResponse,
  CongregationBatch,
  CongregationBatchInput,
  CongregationBatchStatus,
  CongregationBatchEnrollment,
  CongregationBatchEnrollmentInput,
  CongregationBatchEnrollmentStatus,
  CongregationFacilitator,
  CongregationBatchPost,
  CongregationBatchPostInput,
  CongregationBatchPostWithDetails,
  BatchPostReactionType,
  CongregationBatchPostReaction,
  CongregationBatchPostReply,
  CongregationBatchPostReplyInput,
  CongregationBatchPostAttachment,
  CongregationBatchPostAttachmentInput,
  CongregationBatchPostPoll,
  CongregationBatchPostPollInput,
  BatchAttendance,
  BatchAttendanceRecord,
  BatchAttendanceWithRecords,
  BatchAttendanceInput,
  BatchAttendanceStatus,
  CongregationServiceType,
  CongregationService,
  CongregationServiceStatus,
  CongregationServiceInput,
  CongregationFamilyRelation,
  CongregationFamilyCreateRequest,
  CongregationFamilyVisibilityInput,
  CongregationFamilyResponse,
  CongregationManualRelative,
  CongregationManualRelativeInput,
  CongregationManualRelativesResponse,
  CongregationChantingRecord,
  CongregationChantingInput,
  CongregationChantingResponse,
  CongregationBookProgress,
  CongregationBookProgressInput,
  CongregationBooksResponse,
  CongregationBookMaster,
  CongregationBookReading,
  CongregationBookReadingInput,
  CongregationCourseProgress,
  CongregationCourseProgressInput,
  CongregationCoursesResponse,
  CongregationMilestone,
  CongregationMilestoneInput,
  CongregationMilestonesResponse,
  CongregationDocument,
  CongregationDocumentInput,
  CongregationDocumentsResponse,
  CongregationMemberDocument,
  CongregationMemberCourse,
  CongregationCourseVerification,
  CongregationDocumentVerification,
  CongregationBirthdayReportEntry,
  CongregationBirthdayReportResponse,
  CongregationAnniversaryReportEntry,
  CongregationAnniversaryReportResponse,
  CongregationPhoto,
  CongregationPhotoInput,
  CongregationPhotosResponse,
  CongregationHousehold,
  CongregationHouseholdsResponse,
  CongregationHouseholdResponse,
  CongregationMentorNotificationsResponse,
  CongregationMentorAuditEventsResponse,
  SadhanaTemplate,
  SadhanaTemplateListResponse,
  SadhanaTemplateCreateRequest,
  SadhanaTemplateCreateResponse,
  SadhanaTemplateUpdateRequest,
  SadhanaTemplateUpdateResponse,
  SadhanaTemplateDeleteResponse,
  SadhanaTemplateDefaultAssignmentRequest,
  SadhanaTemplateDefaultAssignmentsResult,
  SadhanaTemplateAssignment,
  SadhanaTemplateAssignmentFailure,
  SadhanaTemplateAssignmentsResponse,
  SadhanaTemplateAssignmentRequest,
  SadhanaTemplateAssignmentResponse,
  SadhanaTemplateAssignmentCreateResponse,
  SadhanaTemplateAssignDefaultsResponse,
  SadhanaCardWeek,
  SadhanaWeeksResponse,
  SadhanaWeekDetailResponse,
  SadhanaMentorCommentRequest,
  SadhanaEntryUpsertRequest,
  SadhanaEntryMutationResponse,
  ChantingPrescriptionsResponse,
  ChantingPrescriptionResponse,
  ChantingPrescriptionCreateRequest,
  SadhanaChantingAnalyticsResponse,
} from '../types/congregation';

const sanitizeBaseUrl = (raw?: string | null) => {
  if (!raw) return '';
  return raw.replace(/\/+$/, '');
};

const getBackendUrl = () => sanitizeBaseUrl(process.env.REACT_APP_BACKEND_URL);

const buildBackendUrl = (path: string) => {
  const base = getBackendUrl();
  if (!base) {
    return path.startsWith('/') ? path : `/${path}`;
  }

  try {
    const normalizedBase = base.endsWith('/') ? base : `${base}/`;
    return new URL(path, normalizedBase).toString();
  } catch (error) {
    console.warn('Failed to compose backend URL, falling back to string concat.', error);
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }
};

/**
 * @deprecated Use new ApiError from core/errors.ts instead
 * This is the legacy ApiError class from the old monolithic API
 */
class OldApiError extends Error {
  status: number;

  info?: unknown;

  constructor(message: string, status: number, info?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.info = info;
  }
}

const isOldApiError = (error: unknown): error is OldApiError => error instanceof OldApiError;

const isForbiddenOrNotFound = (error: unknown): boolean => {
  if (isOldApiError(error)) {
    return error.status === 403 || error.status === 404;
  }
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return false;
  }
  const statusValue = (error as { status?: unknown }).status;
  const status =
    typeof statusValue === 'number'
      ? statusValue
      : typeof statusValue === 'string'
      ? Number(statusValue)
      : null;
  if (!Number.isFinite(status)) {
    return false;
  }
  return status === 403 || status === 404;
};

const normalizeCongregationApplication = (
  application: CongregationApplicationApi,
): CongregationApplication => ({
  ...application,
  notes: application.notes ?? null,
  events: application.events,
});

const normalizeCongregationApplications = (
  applications: CongregationApplicationApi[] | undefined,
): CongregationApplication[] => {
  if (!applications?.length) return [];
  return applications.map(normalizeCongregationApplication);
};

const buildCongregationApplicationSubmitRequest = (
  payload: CongregationApplicationSubmitPayload,
): CongregationApplicationSubmitRequest => {
  const { personal, devotional, family, notes } = payload;

  const request: CongregationApplicationSubmitRequest = {
    personal,
    devotional,
  };

  if (family && (family.maritalStatus || family.dependents?.length)) {
    request.family = family;
  }

  if (notes === null) {
    request.notes = null;
  } else if (typeof notes === 'string') {
    const trimmed = notes.trim();
    if (trimmed) {
      request.notes = trimmed;
    }
  }

  return request;
};

type ApiFetchOptions = RequestInit & {
  skipDedupe?: boolean;
  dedupeKey?: string;
};

type InFlightEntry<T> = {
  controller: AbortController;
  promise: Promise<T>;
  consumers: number;
  abortTimeout?: ReturnType<typeof setTimeout>;
};

const inFlightRequests = new Map<string, InFlightEntry<unknown>>();

const getBodyCacheKey = (body: BodyInit | null | undefined) => {
  if (!body) return '';
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof Blob || body instanceof FormData || body instanceof ArrayBuffer) {
    return undefined;
  }
  try {
    return JSON.stringify(body);
  } catch (error) {
    console.warn('Unable to serialise request body for dedupe. Skipping cache.', error);
    return undefined;
  }
};

const buildDedupeKey = (
  url: string,
  options: ApiFetchOptions,
): string | undefined => {
  if (options.skipDedupe) return undefined;
  if (options.dedupeKey) return options.dedupeKey;
  const method = (options.method ?? 'GET').toUpperCase();
  const bodyKey = getBodyCacheKey(options.body ?? null);
  if (bodyKey === undefined && method !== 'GET' && method !== 'HEAD') {
    return undefined;
  }
  return `${method}::${url}${bodyKey ? `::${bodyKey}` : ''}`;
};

function attachAbortCleanup<T>(
  entry: InFlightEntry<T>,
  key: string,
) {
  entry.promise.finally(() => {
    inFlightRequests.delete(key);
    if (entry.abortTimeout) {
      clearTimeout(entry.abortTimeout);
    }
  });
}

export type FetchSadhanaTemplatesParams = {
  includeItems?: boolean;
};

export type FetchSadhanaTemplateAssignmentParams = {
  memberId?: number;
};

export type FetchSadhanaTemplateAssignmentsParams = {
  memberId?: number;
};

export type FetchSadhanaWeeksParams = {
  memberId?: number;
  limit?: number;
  before?: string;
};

export type FetchSadhanaWeekDetailParams = {
  memberId?: number;
};

export type FetchSadhanaCurrentWeekParams = {
  date?: string;
  memberId?: number;
};

export type FetchSadhanaPrescriptionsParams = {
  memberId?: number;
};

export type FetchSadhanaChantingAnalyticsParams = {
  weeks?: number;
  since?: string;
  until?: string;
  devoteeMemberId?: number;
};

const buildEmptySadhanaAnalyticsResponse = (): SadhanaChantingAnalyticsResponse => ({
  report: {
    generatedAt: new Date().toISOString(),
    rangeStart: new Date().toISOString(),
    rangeEnd: new Date().toISOString(),
    cohorts: [],
  },
});

export const shouldFallbackToEmptySadhanaAnalytics = (error: unknown): boolean => {
  return isForbiddenOrNotFound(error);
};

function scheduleAbortIfIdle<T>(entry: InFlightEntry<T>) {
  if (entry.consumers > 0) return;
  if (entry.abortTimeout) {
    clearTimeout(entry.abortTimeout);
  }
  entry.abortTimeout = setTimeout(() => {
    if (entry.consumers === 0) {
      entry.controller.abort();
    }
  }, 120);
}

const resolveUrl = (input: string) => {
  if (/^https?:\/\//i.test(input)) {
    return input;
  }
  return buildBackendUrl(input);
};

const getAuthBaseUrl = () => sanitizeBaseUrl(process.env.REACT_APP_AUTH_WEB_URL);

const parseSuccessPayload = async <T>(response: Response): Promise<T> => {
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }
  try {
    const text = await response.text();
    if (!text) {
      return undefined as T;
    }
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return JSON.parse(text) as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch (error) {
      return text as unknown as T;
    }
  } catch (error) {
    return undefined as T;
  }
};

const createApiError = async (response: Response): Promise<ApiError> => {
  let info: unknown = null;
  let message = 'API request failed';
  try {
    const text = await response.text();
    if (text) {
      try {
        info = JSON.parse(text);
      } catch (error) {
        info = text;
      }
      if (info && typeof info === 'object' && 'message' in info && typeof (info as any).message === 'string') {
        message = (info as any).message;
      } else if (info && typeof info === 'object' && 'error' in info && typeof (info as any).error === 'string') {
        message = (info as any).error;
      } else if (typeof info === 'string') {
        message = info;
      }
    }
  } catch (error) {
    // ignore body parsing errors
  }
  return new ApiError(message, response.status, info);
};

export async function apiFetch<T>(
  url: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const requestUrl = resolveUrl(url);
  const { skipDedupe, dedupeKey, signal, headers, ...rest } = options;
  const finalHeaders = { 'Content-Type': 'application/json', ...(headers || {}) };
  const requestInit: RequestInit = {
    credentials: 'include',
    headers: finalHeaders,
    ...rest,
  };
  const dedupeCacheKey = buildDedupeKey(requestUrl, { ...rest, skipDedupe, dedupeKey });

  const handleResponse = async (response: Response): Promise<T> => {
    if (!response.ok) {
      if (response.status === 401) {
        const authBase = getAuthBaseUrl();
        if (authBase) {
          const redirectUrl = encodeURIComponent(window.location.href);
          window.location.href = `${authBase}/signin?redirect=${redirectUrl}`;
        }
      }
      throw await createApiError(response);
    }
    return parseSuccessPayload<T>(response);
  };

  if (!dedupeCacheKey) {
    const response = await fetch(requestUrl, {
      ...requestInit,
      signal,
    });
    return handleResponse(response);
  }

  let entry = inFlightRequests.get(dedupeCacheKey) as InFlightEntry<T> | undefined;

  if (!entry) {
    const controller = new AbortController();
    entry = {
      controller,
      consumers: 0,
      promise: fetch(requestUrl, {
        ...requestInit,
        signal: controller.signal,
      }).then(handleResponse),
    };
    inFlightRequests.set(dedupeCacheKey, entry as InFlightEntry<unknown>);
    attachAbortCleanup(entry, dedupeCacheKey);
  }

  entry.consumers += 1;
  if (entry.abortTimeout) {
    clearTimeout(entry.abortTimeout);
    entry.abortTimeout = undefined;
  }

  return new Promise<T>((resolve, reject) => {
    let aborted = false;

    const release = () => {
      entry!.consumers -= 1;
      if (entry!.consumers < 0) entry!.consumers = 0;
      scheduleAbortIfIdle(entry!);
    };

    const abortReason = () => {
      if (!signal) return new DOMException('Aborted', 'AbortError');
      const anySignal = signal as AbortSignal & { reason?: unknown };
      return anySignal.reason ?? new DOMException('Aborted', 'AbortError');
    };

    const handleAbort = () => {
      aborted = true;
      release();
      reject(abortReason());
    };

    if (signal) {
      if (signal.aborted) {
        handleAbort();
        return;
      }
      signal.addEventListener('abort', handleAbort, { once: true });
    }

    entry!.promise
      .then((value) => {
        if (aborted) return;
        resolve(value);
      })
      .catch((error) => {
        if (aborted) return;
        reject(error);
      })
      .finally(() => {
        if (signal) {
          signal.removeEventListener('abort', handleAbort);
        }
        if (!aborted) {
          release();
        }
      });
  });
}

/**
 * @deprecated Use `donationService.getDonations()` instead
 * @see src/api/services/donations.ts
 */
export async function fetchDonations(signal?: AbortSignal) {
  return apiFetch(buildBackendUrl('/api/devotee/donation'), { signal });
}


export async function fetchDonationById(id: string, signal?: AbortSignal) {
  return apiFetch(buildBackendUrl(`/api/devotee/donation/${id}`), { signal });
}

export async function fetchEvent(eventId: string, signal?: AbortSignal): Promise<Event> {
  return apiFetch<Event>(buildBackendUrl(`/api/public/event/${eventId}`), { signal });
}

/**
 * @deprecated Use `eventService.getEvents()` instead
 * @see src/api/services/events.ts
 */
export async function fetchEventsList(signal?: AbortSignal): Promise<Event[]> {
  return apiFetch<Event[]>(buildBackendUrl('/api/public/event'), { signal });
}

/**
 * @deprecated Use `articlesService.getArticles()` instead
 * @see src/api/services/articles.ts
 */
export async function fetchArticlesList(signal?: AbortSignal): Promise<Article[]> {
  try {
    return await apiFetch<Article[]>(buildBackendUrl('/api/articles'), { signal });
  } catch (error) {
    if (isOldApiError(error) && error.status === 404) {
      return [];
    }
    throw error;
  }
}

export async function fetchArticleDetail(id: number | string, signal?: AbortSignal): Promise<Article> {
  return apiFetch<Article>(buildBackendUrl(`/api/articles/${id}`), { signal });
}

/**
 * @deprecated Use `quoteService.getQuoteOfTheDay()` instead
 * @see src/api/services/quotes.ts
 */
export async function fetchTodayQuote(signal?: AbortSignal): Promise<QuoteOfTheDay | null> {
  const requestUrl = buildBackendUrl('/api/quote/today');
  try {
    const response = await fetch(requestUrl, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      signal,
    });

    // Handle 404 - quote not found (expected case)
    if (response.status === 404) {
      return null;
    }

    // Handle other errors - also return null instead of throwing
    if (!response.ok) {
      return null;
    }

    return parseSuccessPayload<QuoteOfTheDay>(response);
  } catch (error) {
    // Any fetch error - return null
    return null;
  }
}

export async function fetchAnnouncements(signal?: AbortSignal): Promise<Announcement[]> {
  try {
    return await apiFetch<Announcement[]>(buildBackendUrl('/api/announcements'), { signal });
  } catch (error) {
    if (isOldApiError(error) && error.status === 404) {
      return [];
    }
    throw error;
  }
}

/**
 * @deprecated Use `userService.getCurrentUser()` instead
 * @see src/api/services/user.ts
 */
export async function fetchCurrentUser(signal?: AbortSignal): Promise<User> {
  return apiFetch<User>(buildBackendUrl('/api/user/me'), { signal });
}

export async function fetchCongregationMe(signal?: AbortSignal): Promise<CongregationMeResponse> {
  const url = buildBackendUrl('/api/congregation/me');
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    signal,
  });

  // Handle 403 Forbidden (user not a member) - return safe default WITHOUT throwing
  if (response.status === 403) {
    return { member: null, roles: [] };
  }

  // Handle 404 Not Found - return safe default WITHOUT throwing
  if (response.status === 404) {
    return { member: null, roles: [] };
  }

  // Handle other error responses - return safe default instead of throwing
  if (!response.ok) {
    return { member: null, roles: [] };
  }

  // Success - parse and return response
  return parseSuccessPayload<CongregationMeResponse>(response);
}

export async function fetchCongregationMasters(
  signal?: AbortSignal,
): Promise<CongregationMastersPayload> {
  try {
    return await apiFetch<CongregationMastersPayload>(buildBackendUrl('/api/public/congregation/masters'), {
      signal,
    });
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return {
        centers: [],
        spiritualGuides: [],
        spiritualMasters: [],
        courses: [],
        books: [],
        skills: [],
        eligibility: null,
      };
    }
    throw error;
  }
}

export interface FetchCongregationProfileParams {
  memberId?: number;
}

export async function fetchCongregationProfile(
  params: FetchCongregationProfileParams = {},
  signal?: AbortSignal,
): Promise<CongregationProfileSnapshot> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query ? `/api/congregation/profile?${query}` : '/api/congregation/profile';
  return apiFetch<CongregationProfileSnapshot>(buildBackendUrl(path), { signal });
}

export interface FetchCongregationProfilesBatchParams {
  memberIds: number[];
}

export async function fetchCongregationProfilesBatch(
  params: FetchCongregationProfilesBatchParams,
  signal?: AbortSignal,
): Promise<CongregationProfileSnapshot[]> {
  const memberIds = (params.memberIds ?? []).filter((id) => Number.isFinite(id));
  const searchParams = new URLSearchParams();
  if (memberIds.length > 0) {
    searchParams.set('memberIds', memberIds.join(','));
  }
  const query = searchParams.toString();
  const path = query ? `/api/congregation/profiles?${query}` : '/api/congregation/profiles';
  const response = await apiFetch<{ profiles: CongregationProfileSnapshot[] }>(
    buildBackendUrl(path),
    { signal },
  );
  return response.profiles ?? [];
}

export interface CongregationMemberSearchResult {
  member: CongregationMember;
  mentorSummary?: {
    devoteeMemberId: number;
    mentorMemberId?: number;
    mentorName?: string;
    role?: string;
  };
}

export interface CongregationMemberSearchOptions {
  centerId?: string | number;
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  gender?: string;
  education?: string;
  maritalStatus?: string;
  initiated?: boolean;
  verifiedEmail?: boolean;
  mentorId?: number;
  batchId?: number;
  courseId?: number;
  serviceId?: number;
  occupation?: string;
  initiationStatus?: string;
  ageMin?: number;
  ageMax?: number;
}

export async function fetchAllAdminMembers(
  options: CongregationMemberSearchOptions = {},
  signal?: AbortSignal,
): Promise<{ results: CongregationMemberSearchResult[]; total?: number }> {
  const params = new URLSearchParams();
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  if (typeof options.offset === 'number') {
    params.set('offset', String(options.offset));
  }
  if (options.search) {
    params.set('search', options.search);
  }
  if (options.status) {
    params.set('status', options.status);
  }
  if (options.gender) {
    params.set('gender', options.gender);
  }
  if (options.education) {
    params.set('education', options.education);
  }
  if (options.maritalStatus) {
    params.set('maritalStatus', options.maritalStatus);
  }
  if (options.initiated) {
    params.set('initiated', String(options.initiated));
  }
  if (options.verifiedEmail) {
    params.set('verifiedEmail', String(options.verifiedEmail));
  }
  if (options.mentorId) {
    params.set('mentorId', String(options.mentorId));
  }
  if (options.batchId) {
    params.set('batchId', String(options.batchId));
  }
  if (options.courseId) {
    params.set('courseId', String(options.courseId));
  }
  if (options.serviceId) {
    params.set('serviceId', String(options.serviceId));
  }
  if (options.occupation) {
    params.set('occupation', options.occupation);
  }
  if (options.initiationStatus) {
    params.set('initiationStatus', options.initiationStatus);
  }
  if (options.ageMin) {
    params.set('ageMin', String(options.ageMin));
  }
  if (options.ageMax) {
    params.set('ageMax', String(options.ageMax));
  }
  const query = params.toString();
  return apiFetch<{ results: CongregationMemberSearchResult[]; total?: number }>(
    buildBackendUrl(`/api/congregation/admin/members${query ? `?${query}` : ''}`),
    { signal },
  );
}

export async function fetchAllMentorMembers(
  options: CongregationMemberSearchOptions = {},
  signal?: AbortSignal,
): Promise<{ results: CongregationMemberSearchResult[]; total?: number }> {
  const params = new URLSearchParams();
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  if (typeof options.offset === 'number') {
    params.set('offset', String(options.offset));
  }
  if (options.search) {
    params.set('search', options.search);
  }
  if (options.status) {
    params.set('status', options.status);
  }
  if (options.gender) {
    params.set('gender', options.gender);
  }
  if (options.education) {
    params.set('education', options.education);
  }
  if (options.maritalStatus) {
    params.set('maritalStatus', options.maritalStatus);
  }
  if (options.initiated) {
    params.set('initiated', String(options.initiated));
  }
  if (options.verifiedEmail) {
    params.set('verifiedEmail', String(options.verifiedEmail));
  }
  if (options.batchId) {
    params.set('batchId', String(options.batchId));
  }
  if (options.courseId) {
    params.set('courseId', String(options.courseId));
  }
  if (options.serviceId) {
    params.set('serviceId', String(options.serviceId));
  }
  if (options.occupation) {
    params.set('occupation', options.occupation);
  }
  if (options.initiationStatus) {
    params.set('initiationStatus', options.initiationStatus);
  }
  if (options.ageMin) {
    params.set('ageMin', String(options.ageMin));
  }
  if (options.ageMax) {
    params.set('ageMax', String(options.ageMax));
  }
  const query = params.toString();
  return apiFetch<{ results: CongregationMemberSearchResult[]; total?: number }>(
    buildBackendUrl(`/api/congregation/mentor/members${query ? `?${query}` : ''}`),
    { signal },
  );
}

export interface AdminAllDevoteesResponse {
  devotees: Array<{
    memberId: number;
    name: string;
    email?: string;
    status?: string;
  }>;
  total?: number;
}

export async function listAdminAllDevotees(
  options: CongregationMemberSearchOptions = {},
  signal?: AbortSignal,
): Promise<AdminAllDevoteesResponse> {
  try {
    // Default to 100 per page for scalability
    const limit = options.limit || 100;
    const offset = options.offset || 0;
    
    const result = await fetchAllAdminMembers({ ...options, limit, offset }, signal);

    const devotees = (result.results || [])
      .map((m: any) => ({
        memberId: m?.member?.id,
        name: m?.member?.displayName || (m?.member?.id ? `Member #${m.member.id}` : 'Member'),
        email: m?.member?.email || undefined,
        status: m?.member?.status || 'INACTIVE',
      }))
      .filter((d: any) => d.memberId != null);

    return { devotees, total: result.total };
  } catch (error) {
    console.error('Failed to fetch admin devotees:', error);
    throw error;
  }
}

export async function listMentorAllDevotees(
  options: CongregationMemberSearchOptions = {},
  signal?: AbortSignal,
): Promise<AdminAllDevoteesResponse> {
  try {
    const limit = options.limit || 100;
    const offset = options.offset || 0;

    const result = await fetchAllMentorMembers({ ...options, limit, offset }, signal);

    const devotees = (result.results || [])
      .map((m: any) => ({
        memberId: m?.member?.id,
        name: m?.member?.displayName || (m?.member?.id ? `Member #${m.member.id}` : 'Member'),
        email: m?.member?.email || undefined,
        status: m?.member?.status || 'INACTIVE',
      }))
      .filter((d: any) => d.memberId != null);

    return { devotees, total: result.total };
  } catch (error) {
    console.error('Failed to fetch mentor devotees:', error);
    throw error;
  }
}

export async function fetchCongregationApplications(
  signal?: AbortSignal,
): Promise<CongregationApplicationsResponse> {
  try {
    const response = await apiFetch<CongregationApplicationsApiResponse>(
      buildBackendUrl('/api/congregation/applications'),
      { signal },
    );
    return { applications: normalizeCongregationApplications(response.applications) };
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return { applications: [] };
    }
    throw error;
  }
}

export async function fetchCongregationApplicationDetail(
  applicationId: number,
  signal?: AbortSignal,
): Promise<CongregationApplication> {
  const response = await apiFetch<{ application: CongregationApplicationApi }>(
    buildBackendUrl(`/api/congregation/applications/${applicationId}`),
    { signal },
  );
  return normalizeCongregationApplication(response.application);
}

export async function submitCongregationApplication(
  payload: CongregationApplicationSubmitPayload,
): Promise<CongregationApplication> {
  const requestBody = {
    formData: buildCongregationApplicationSubmitRequest(payload),
    preferredMentorId: payload.preferredMentorId,
  };
  const response = await apiFetch<{ application: CongregationApplicationApi }>(
    buildBackendUrl('/api/congregation/applications'),
    {
      method: 'POST',
      body: JSON.stringify(requestBody),
    },
  );
  return normalizeCongregationApplication(response.application);
}

export async function withdrawCongregationApplication(
  applicationId: number,
  signal?: AbortSignal,
): Promise<CongregationApplication> {
  const response = await apiFetch<{ application: CongregationApplicationApi }>(
    buildBackendUrl(`/api/congregation/applications/${applicationId}/withdraw`),
    { method: 'POST', signal },
  );
  return normalizeCongregationApplication(response.application);
}

export interface FetchMentorApplicationsParams {
  statuses?: string[];
  search?: string;
}

export async function fetchMentorApplications(
  params?: FetchMentorApplicationsParams,
  signal?: AbortSignal,
): Promise<CongregationMentorApplicationsResponse> {
  try {
    const searchParams = new URLSearchParams();
    if (params?.statuses?.length) {
      searchParams.set('status', Array.from(new Set(params.statuses)).join(','));
    }
    if (params?.search) {
      const trimmed = params.search.trim();
      if (trimmed) {
        searchParams.set('search', trimmed);
      }
    }

    const query = searchParams.toString();
    const path = query
      ? `/api/congregation/mentor/applications?${query}`
      : '/api/congregation/mentor/applications';

    const response = await apiFetch<CongregationApplicationsApiResponse>(buildBackendUrl(path), {
      signal,
    });
    return { applications: normalizeCongregationApplications(response.applications) };
  } catch (error) {
    if (isForbiddenOrNotFound(error)) {
      return { applications: [] };
    }
    throw error;
  }
}

export async function fetchMentorNotifications(
  signal?: AbortSignal,
): Promise<CongregationMentorNotificationsResponse> {
  try {
    const response = await apiFetch<CongregationMentorNotificationsResponse>(
      buildBackendUrl('/api/congregation/mentor/notifications'),
      { signal, dedupeKey: 'mentor-notifications' },
    );
    return { notifications: response.notifications ?? [] };
  } catch (error) {
    if (isForbiddenOrNotFound(error)) {
      return { notifications: [] };
    }
    throw error;
  }
}

export async function fetchMentorAuditEvents(
  signal?: AbortSignal,
): Promise<CongregationMentorAuditEventsResponse> {
  try {
    const response = await apiFetch<CongregationMentorAuditEventsResponse>(
      buildBackendUrl('/api/congregation/mentor/audit-events'),
      { signal, dedupeKey: 'mentor-audit-events' },
    );
    return { events: response.events ?? [] };
  } catch (error) {
    if (isOldApiError(error) && (error.status === 403 || error.status === 404)) {
      return { events: [] };
    }
    throw error;
  }
}

export async function fetchSadhanaTemplates(
  params: FetchSadhanaTemplatesParams = {},
  signal?: AbortSignal,
): Promise<SadhanaTemplateListResponse> {
  const searchParams = new URLSearchParams();
  if (params.includeItems !== undefined) {
    searchParams.set('includeItems', params.includeItems ? 'true' : 'false');
  }
  const query = searchParams.toString();
  const path = query ? `/api/congregation/sadhana/templates?${query}` : '/api/congregation/sadhana/templates';
  const response = await apiFetch<SadhanaTemplateListResponse>(buildBackendUrl(path), { signal });
  return { templates: response.templates ?? [] };
}

export async function createSadhanaTemplate(
  payload: SadhanaTemplateCreateRequest,
  signal?: AbortSignal,
): Promise<SadhanaTemplateCreateResponse> {
  const response = await apiFetch<SadhanaTemplateCreateResponse>(
    buildBackendUrl('/api/congregation/sadhana/templates'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  let defaultAssignments: SadhanaTemplateDefaultAssignmentsResult | null = null;
  if (response.defaultAssignments) {
    defaultAssignments = {
      created: response.defaultAssignments.created ?? [],
      failures: response.defaultAssignments.failures ?? [],
      skippedReason: response.defaultAssignments.skippedReason ?? null,
    };
  }
  return {
    template: response.template,
    defaultAssignments,
  };
}

export async function updateSadhanaTemplate(
  templateId: number,
  payload: SadhanaTemplateUpdateRequest,
  signal?: AbortSignal,
): Promise<SadhanaTemplateUpdateResponse> {
  const response = await apiFetch<SadhanaTemplateUpdateResponse>(
    buildBackendUrl(`/api/congregation/sadhana/templates/${templateId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return { template: response.template };
}

export async function deleteSadhanaTemplate(
  templateId: number,
  signal?: AbortSignal,
): Promise<SadhanaTemplateDeleteResponse> {
  const response = await apiFetch<SadhanaTemplateDeleteResponse>(
    buildBackendUrl(`/api/congregation/sadhana/templates/${templateId}`),
    {
      method: 'DELETE',
      signal,
    },
  );
  return { template: response.template };
}

export async function fetchSadhanaTemplateAssignment(
  params: FetchSadhanaTemplateAssignmentParams = {},
  signal?: AbortSignal,
): Promise<SadhanaTemplateAssignmentResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/templates/assignment?${query}`
    : '/api/congregation/sadhana/templates/assignment';
  return apiFetch<SadhanaTemplateAssignmentResponse>(buildBackendUrl(path), { signal });
}

export async function fetchSadhanaTemplateAssignments(
  params: FetchSadhanaTemplateAssignmentsParams = {},
  signal?: AbortSignal,
): Promise<SadhanaTemplateAssignmentsResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/templates/assignments?${query}`
    : '/api/congregation/sadhana/templates/assignments';
  const response = await apiFetch<SadhanaTemplateAssignmentsResponse>(buildBackendUrl(path), { signal });
  return { assignments: response.assignments ?? [] };
}

export async function createSadhanaTemplateAssignment(
  params: FetchSadhanaTemplateAssignmentsParams = {},
  payload: SadhanaTemplateAssignmentRequest,
  signal?: AbortSignal,
): Promise<SadhanaTemplateAssignmentCreateResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/templates/assignments?${query}`
    : '/api/congregation/sadhana/templates/assignments';
  return apiFetch<SadhanaTemplateAssignmentCreateResponse>(buildBackendUrl(path), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function assignSadhanaTemplateDefaults(
  templateId: number,
  payload: SadhanaTemplateDefaultAssignmentRequest,
  signal?: AbortSignal,
): Promise<SadhanaTemplateAssignDefaultsResponse> {
  const response = await apiFetch<SadhanaTemplateAssignDefaultsResponse>(
    buildBackendUrl(`/api/congregation/sadhana/templates/${templateId}/defaults`),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return {
    template: response.template,
    created: response.created ?? [],
    failures: response.failures ?? [],
    skippedReason: response.skippedReason ?? null,
  };
}

export async function fetchSadhanaWeeks(
  params: FetchSadhanaWeeksParams = {},
  signal?: AbortSignal,
): Promise<SadhanaWeeksResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  if (typeof params.limit === 'number') {
    searchParams.set('limit', String(params.limit));
  }
  if (params.before) {
    searchParams.set('before', params.before);
  }
  const query = searchParams.toString();
  const path = query ? `/api/congregation/sadhana/weeks?${query}` : '/api/congregation/sadhana/weeks';
  try {
    const response = await apiFetch<SadhanaWeeksResponse>(buildBackendUrl(path), { signal });
    return {
      weeks: response.weeks ?? [],
      hasMore: Boolean(response.hasMore),
      nextBefore: response.nextBefore ?? null,
    };
  } catch (error) {
    if (isForbiddenOrNotFound(error)) {
      return {
        weeks: [],
        hasMore: false,
        nextBefore: null,
      };
    }
    throw error;
  }
}

export async function fetchSadhanaWeekDetail(
  weekId: number,
  params: FetchSadhanaWeekDetailParams = {},
  signal?: AbortSignal,
): Promise<SadhanaWeekDetailResponse | null> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/weeks/${weekId}?${query}`
    : `/api/congregation/sadhana/weeks/${weekId}`;
  try {
    return await apiFetch<SadhanaWeekDetailResponse>(buildBackendUrl(path), { signal });
  } catch (error) {
    if (isForbiddenOrNotFound(error)) {
      return null;
    }
    throw error;
  }
}

export async function fetchSadhanaCurrentWeek(
  params: FetchSadhanaCurrentWeekParams = {},
  signal?: AbortSignal,
): Promise<SadhanaWeekDetailResponse | null> {
  const searchParams = new URLSearchParams();
  if (params.date) {
    searchParams.set('date', params.date);
  }
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/weeks/current?${query}`
    : '/api/congregation/sadhana/weeks/current';
  try {
    return await apiFetch<SadhanaWeekDetailResponse>(buildBackendUrl(path), { signal });
  } catch (error) {
    if (isForbiddenOrNotFound(error)) {
      return null;
    }
    throw error;
  }
}

export async function upsertSadhanaEntry(
  weekId: number,
  payload: SadhanaEntryUpsertRequest,
  params: FetchSadhanaWeekDetailParams = {},
  signal?: AbortSignal,
): Promise<SadhanaEntryMutationResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/weeks/${weekId}/entries?${query}`
    : `/api/congregation/sadhana/weeks/${weekId}/entries`;
  return apiFetch<SadhanaEntryMutationResponse>(buildBackendUrl(path), {
    method: 'PUT',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function updateSadhanaMentorComment(
  weekId: number,
  payload: SadhanaMentorCommentRequest,
  signal?: AbortSignal,
): Promise<{ week: SadhanaCardWeek }> {
  return apiFetch<{ week: SadhanaCardWeek }>(
    buildBackendUrl(`/api/congregation/sadhana/weeks/${weekId}/mentor-comment`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
}

export async function fetchSadhanaPrescriptions(
  params: FetchSadhanaPrescriptionsParams = {},
  signal?: AbortSignal,
): Promise<ChantingPrescriptionsResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/prescriptions?${query}`
    : '/api/congregation/sadhana/prescriptions';
  try {
    const response = await apiFetch<ChantingPrescriptionsResponse>(buildBackendUrl(path), { signal });
    return { prescriptions: response.prescriptions ?? [] };
  } catch (error) {
    if (isOldApiError(error) && (error.status === 403 || error.status === 404)) {
      return { prescriptions: [] };
    }
    throw error;
  }
}

export async function createSadhanaPrescription(
  params: FetchSadhanaPrescriptionsParams = {},
  payload: ChantingPrescriptionCreateRequest,
  signal?: AbortSignal,
): Promise<ChantingPrescriptionResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/sadhana/prescriptions?${query}`
    : '/api/congregation/sadhana/prescriptions';
  return apiFetch<ChantingPrescriptionResponse>(buildBackendUrl(path), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function fetchSadhanaChantingAnalytics(
  params: FetchSadhanaChantingAnalyticsParams = {},
  signal?: AbortSignal,
): Promise<SadhanaChantingAnalyticsResponse> {
  const searchParams = new URLSearchParams();
  if (typeof params.weeks === 'number') {
    searchParams.set('weeks', String(params.weeks));
  }
  if (params.since) {
    searchParams.set('since', params.since);
  }
  if (params.until) {
    searchParams.set('until', params.until);
  }
  if (typeof params.devoteeMemberId === 'number') {
    searchParams.set('devoteeMemberId', String(params.devoteeMemberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/mentor/sadhana/analytics/chanting?${query}`
    : '/api/congregation/mentor/sadhana/analytics/chanting';
  return apiFetch<SadhanaChantingAnalyticsResponse>(buildBackendUrl(path), { signal }).catch((error) => {
    if (shouldFallbackToEmptySadhanaAnalytics(error)) {
      return buildEmptySadhanaAnalyticsResponse();
    }
    throw error;
  });
}

const mentorDecisionFetch = async (
  path: string,
  payload: CongregationMentorDecisionInput | CongregationMentorRecommendationInput,
  signal?: AbortSignal,
): Promise<CongregationApplication> => {
  const response = await apiFetch<{ application: CongregationApplicationApi }>(buildBackendUrl(path), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
  return normalizeCongregationApplication(response.application);
};

export async function acceptMentorApplication(
  applicationId: number,
  payload: CongregationMentorDecisionInput,
  signal?: AbortSignal,
): Promise<CongregationApplication> {
  return mentorDecisionFetch(`/api/congregation/mentor/applications/${applicationId}/accept`, payload, signal);
}

export async function rejectMentorApplication(
  applicationId: number,
  payload: CongregationMentorDecisionInput,
  signal?: AbortSignal,
): Promise<CongregationApplication> {
  return mentorDecisionFetch(`/api/congregation/mentor/applications/${applicationId}/reject`, payload, signal);
}

export async function recommendMentorApplication(
  applicationId: number,
  payload: CongregationMentorRecommendationInput,
  signal?: AbortSignal,
): Promise<CongregationApplication> {
  return mentorDecisionFetch(`/api/congregation/mentor/applications/${applicationId}/recommend`, payload, signal);
}

export interface PhoneCheckResult {
  exists: boolean;
  status: 'new_account' | 'existing_user' | 'already_active' | 'pending_application';
  phone: string;
  userId?: number;
  memberId?: number;
  legalName?: string;
  email?: string;
  membershipStatus?: string;
  applicationId?: number;
  applicationStatus?: string;
}

export async function checkPhoneForDevotee(
  phone: string,
  signal?: AbortSignal,
): Promise<PhoneCheckResult> {
  const response = await apiFetch<PhoneCheckResult>(
    buildBackendUrl('/api/congregation/mentor/devotees/check-phone'),
    {
      method: 'POST',
      body: JSON.stringify({ phone }),
      signal,
    },
  );
  return response;
}

export interface PublicPhoneCheckResult {
  exists: boolean;
  status: 'new_account' | 'existing_user' | 'already_active' | 'pending_application';
  phone: string;
  userId?: number;
  memberId?: number;
  legalName?: string;
  email?: string;
  membershipStatus?: string;
  applicationId?: number;
  applicationStatus?: string;
}

export async function checkPhoneForPublicCongregation(
  phone: string,
  signal?: AbortSignal,
): Promise<PublicPhoneCheckResult> {
  const response = await apiFetch<PublicPhoneCheckResult>(
    buildBackendUrl('/api/public/congregation/check-phone'),
    {
      method: 'POST',
      body: JSON.stringify({ phone }),
      signal,
    },
  );
  return response;
}

export interface PublicCreateAccountPayload {
  phone: string;
  displayName: string;
  email: string;
  password: string;
}

export async function createPublicCongregationAccount(
  payload: PublicCreateAccountPayload,
  signal?: AbortSignal,
): Promise<{ success: boolean; userId: number; message?: string }> {
  const response = await apiFetch<{ success: boolean; userId: number; message?: string }>(
    buildBackendUrl('/api/public/congregation/create-account'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response;
}

export async function submitPublicCongregationApplication(
  phone: string,
  payload: CongregationApplicationSubmitPayload,
  signal?: AbortSignal,
): Promise<CongregationApplication> {
  const requestBody = {
    phone,
    formData: buildCongregationApplicationSubmitRequest(payload),
    preferredMentorId: payload.preferredMentorId ?? null,
  };
  const response = await apiFetch<{ application: CongregationApplicationApi }>(
    buildBackendUrl('/api/public/congregation/applications'),
    {
      method: 'POST',
      body: JSON.stringify(requestBody),
      signal,
    },
  );
  return normalizeCongregationApplication(response.application);
}

export async function createMentorDevotee(
  payload: CongregationMentorDevoteeCreatePayload,
  signal?: AbortSignal,
): Promise<CongregationApplication> {
  const response = await apiFetch<{ application: CongregationApplicationApi }>(
    buildBackendUrl('/api/congregation/mentor/devotees'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return normalizeCongregationApplication(response.application);
}

export interface UpdateCongregationPersonalParams {
  memberId?: number;
}

export async function updateCongregationPersonalProfile(
  payload: CongregationMemberPersonalInput,
  params: UpdateCongregationPersonalParams = {},
  signal?: AbortSignal,
): Promise<CongregationMember> {
  const searchParams = new URLSearchParams();
  if (typeof params.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/profile/personal?${query}`
    : '/api/congregation/profile/personal';
  const response = await apiFetch<{ member: CongregationMember }>(buildBackendUrl(path), {
    method: 'PUT',
    body: JSON.stringify(payload),
    signal,
  });
  return response.member;
}

export async function fetchCongregationContacts(
  signal?: AbortSignal,
): Promise<CongregationContactPoint[]> {
  try {
    const response = await apiFetch<CongregationContactsResponse>(
      buildBackendUrl('/api/congregation/contacts'),
      { signal },
    );
    return response.contacts ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationContact(
  payload: CongregationContactInput,
  signal?: AbortSignal,
): Promise<CongregationContactPoint> {
  const response = await apiFetch<{ contact: CongregationContactPoint }>(
    buildBackendUrl('/api/congregation/contacts'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.contact;
}

export async function updateCongregationContact(
  contactId: number,
  payload: CongregationContactInput,
  signal?: AbortSignal,
): Promise<CongregationContactPoint> {
  const response = await apiFetch<{ contact: CongregationContactPoint }>(
    buildBackendUrl(`/api/congregation/contacts/${contactId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.contact;
}

export async function deleteCongregationContact(
  contactId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/contacts/${contactId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function verifyCongregationContact(
  contactId: number,
  signal?: AbortSignal,
): Promise<CongregationContactPoint> {
  const response = await apiFetch<{ contact: CongregationContactPoint }>(
    buildBackendUrl(`/api/congregation/contacts/${contactId}/verify`),
    {
      method: 'POST',
      signal,
    },
  );
  return response.contact;
}

export async function fetchCongregationAddresses(
  signal?: AbortSignal,
): Promise<CongregationAddress[]> {
  try {
    const response = await apiFetch<CongregationAddressesResponse>(
      buildBackendUrl('/api/congregation/addresses'),
      { signal },
    );
    return response.addresses ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationAddress(
  payload: CongregationAddressInput,
  signal?: AbortSignal,
): Promise<CongregationAddress> {
  const response = await apiFetch<{ address: CongregationAddress }>(
    buildBackendUrl('/api/congregation/addresses'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.address;
}

export async function updateCongregationAddress(
  addressId: number,
  payload: CongregationAddressInput,
  signal?: AbortSignal,
): Promise<CongregationAddress> {
  const response = await apiFetch<{ address: CongregationAddress }>(
    buildBackendUrl(`/api/congregation/addresses/${addressId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.address;
}

export async function deleteCongregationAddress(
  addressId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/addresses/${addressId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function fetchCongregationFamily(
  signal?: AbortSignal,
): Promise<CongregationFamilyRelation[]> {
  try {
    const response = await apiFetch<CongregationFamilyResponse | { relations?: CongregationFamilyRelation[] }>(
      buildBackendUrl('/api/congregation/family'),
      { signal },
    );
    // Support both legacy { family: [...] } and current { relations: [...] } shapes
    const maybeRelations = (response as any).relations;
    if (Array.isArray(maybeRelations)) {
      return maybeRelations as CongregationFamilyRelation[];
    }
    return (response as CongregationFamilyResponse).family ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationFamilyRelation(
  payload: CongregationFamilyCreateRequest,
  signal?: AbortSignal,
): Promise<CongregationFamilyRelation> {
  const response = await apiFetch<{ relation: CongregationFamilyRelation }>(
    buildBackendUrl('/api/congregation/family'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.relation;
}

export async function confirmCongregationFamilyRelation(
  relationId: number,
  signal?: AbortSignal,
): Promise<CongregationFamilyRelation> {
  const response = await apiFetch<{ relation: CongregationFamilyRelation }>(
    buildBackendUrl(`/api/congregation/family/${relationId}/confirm`),
    {
      method: 'POST',
      signal,
    },
  );
  return response.relation;
}

export async function rejectCongregationFamilyRelation(
  relationId: number,
  signal?: AbortSignal,
): Promise<CongregationFamilyRelation> {
  const response = await apiFetch<{ relation: CongregationFamilyRelation }>(
    buildBackendUrl(`/api/congregation/family/${relationId}/reject`),
    {
      method: 'POST',
      signal,
    },
  );
  return response.relation;
}

export async function cancelCongregationFamilyRelation(
  relationId: number,
  signal?: AbortSignal,
): Promise<CongregationFamilyRelation> {
  const response = await apiFetch<{ relation: CongregationFamilyRelation }>(
    buildBackendUrl(`/api/congregation/family/${relationId}/cancel`),
    {
      method: 'POST',
      signal,
    },
  );
  return response.relation;
}

export async function updateCongregationFamilyVisibility(
  relationId: number,
  payload: CongregationFamilyVisibilityInput,
  signal?: AbortSignal,
): Promise<CongregationFamilyRelation> {
  const response = await apiFetch<{ relation: CongregationFamilyRelation }>(
    buildBackendUrl(`/api/congregation/family/${relationId}/visibility`),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.relation;
}

// Manual Relatives (non-member family)

export async function fetchManualRelatives(
  signal?: AbortSignal,
): Promise<CongregationManualRelative[]> {
  try {
    const response = await apiFetch<CongregationManualRelativesResponse>(
      buildBackendUrl('/api/congregation/family/manual'),
      { signal },
    );
    return response.relatives ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createManualRelative(
  payload: CongregationManualRelativeInput,
  signal?: AbortSignal,
): Promise<CongregationManualRelative> {
  const response = await apiFetch<{ relative: CongregationManualRelative }>(
    buildBackendUrl('/api/congregation/family/manual'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.relative;
}

export async function updateManualRelative(
  relativeId: number,
  payload: CongregationManualRelativeInput,
  signal?: AbortSignal,
): Promise<CongregationManualRelative> {
  const response = await apiFetch<{ relative: CongregationManualRelative }>(
    buildBackendUrl(`/api/congregation/family/manual/${relativeId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.relative;
}

export async function deleteManualRelative(
  relativeId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<{ message: string }>(
    buildBackendUrl(`/api/congregation/family/manual/${relativeId}`),
    {
      method: 'DELETE',
      signal,
    },
  );
}

export async function fetchCongregationChanting(
  signal?: AbortSignal,
): Promise<CongregationChantingRecord[]> {
  try {
    const response = await apiFetch<CongregationChantingResponse>(
      buildBackendUrl('/api/congregation/chanting'),
      { signal },
    );
    return response.chanting ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationChanting(
  payload: CongregationChantingInput,
  signal?: AbortSignal,
): Promise<CongregationChantingRecord> {
  const response = await apiFetch<{ chanting: CongregationChantingRecord }>(
    buildBackendUrl('/api/congregation/chanting'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.chanting;
}

export async function updateCongregationChanting(
  recordId: number,
  payload: CongregationChantingInput,
  signal?: AbortSignal,
): Promise<CongregationChantingRecord> {
  const response = await apiFetch<{ chanting: CongregationChantingRecord }>(
    buildBackendUrl(`/api/congregation/chanting/${recordId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.chanting;
}

export async function deleteCongregationChanting(
  recordId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/chanting/${recordId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function fetchCongregationBooks(
  signal?: AbortSignal,
): Promise<CongregationBookProgress[]> {
  try {
    const response = await apiFetch<CongregationBooksResponse>(
      buildBackendUrl('/api/congregation/books'),
      { signal },
    );
    return response.books ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationBookReading(
  payload: CongregationBookReadingInput,
  signal?: AbortSignal,
): Promise<CongregationBookReading> {
  const response = await apiFetch<{ reading: CongregationBookReading }>(
    buildBackendUrl('/api/congregation/book-readings'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.reading;
}

export async function updateCongregationBookReading(
  readingId: number,
  payload: CongregationBookReadingInput,
  signal?: AbortSignal,
): Promise<CongregationBookReading> {
  const response = await apiFetch<{ reading: CongregationBookReading }>(
    buildBackendUrl(`/api/congregation/book-readings/${readingId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.reading;
}

export async function deleteCongregationBookReading(
  readingId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/book-readings/${readingId}`), {
    method: 'DELETE',
    signal,
  });
}

// Legacy functions (deprecated - use book-readings above instead)
export async function upsertCongregationBook(
  payload: CongregationBookProgressInput,
  signal?: AbortSignal,
): Promise<CongregationBookProgress> {
  const response = await apiFetch<{ book: CongregationBookProgress }>(
    buildBackendUrl('/api/congregation/books'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.book;
}

export async function deleteCongregationBook(
  progressId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/books/${progressId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function fetchCongregationCourses(
  signal?: AbortSignal,
): Promise<CongregationCourseProgress[]> {
  try {
    const response = await apiFetch<CongregationCoursesResponse>(
      buildBackendUrl('/api/congregation/courses'),
      { signal },
    );
    return response.courses ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function upsertCongregationCourse(
  payload: CongregationCourseProgressInput,
  signal?: AbortSignal,
): Promise<CongregationCourseProgress> {
  const response = await apiFetch<{ course: CongregationCourseProgress }>(
    buildBackendUrl('/api/congregation/courses'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.course;
}

export async function deleteCongregationCourse(
  courseProgressId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/courses/${courseProgressId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function verifyCongregationCourse(
  courseProgressId: number,
  payload: CongregationCourseVerification = { verified: true },
  signal?: AbortSignal,
): Promise<{ course: CongregationMemberCourse }> {
  return apiFetch<{ course: CongregationMemberCourse }>(buildBackendUrl(`/api/congregation/courses/${courseProgressId}/verify`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function fetchCongregationMilestones(
  signal?: AbortSignal,
): Promise<CongregationMilestone[]> {
  try {
    const response = await apiFetch<CongregationMilestonesResponse>(
      buildBackendUrl('/api/congregation/milestones'),
      { signal },
    );
    return response.milestones ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationMilestone(
  payload: CongregationMilestoneInput,
  signal?: AbortSignal,
): Promise<CongregationMilestone> {
  const response = await apiFetch<{ milestone: CongregationMilestone }>(
    buildBackendUrl('/api/congregation/milestones'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.milestone;
}

export async function updateCongregationMilestone(
  milestoneId: number,
  payload: CongregationMilestoneInput,
  signal?: AbortSignal,
): Promise<CongregationMilestone> {
  const response = await apiFetch<{ milestone: CongregationMilestone }>(
    buildBackendUrl(`/api/congregation/milestones/${milestoneId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.milestone;
}

export async function deleteCongregationMilestone(
  milestoneId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/milestones/${milestoneId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function fetchCongregationDocuments(
  signal?: AbortSignal,
): Promise<CongregationDocument[]> {
  try {
    const response = await apiFetch<CongregationDocumentsResponse>(
      buildBackendUrl('/api/congregation/documents'),
      { signal },
    );
    return response.documents ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationDocument(
  payload: CongregationDocumentInput,
  signal?: AbortSignal,
): Promise<CongregationDocument> {
  const response = await apiFetch<{ document: CongregationDocument }>(
    buildBackendUrl('/api/congregation/documents'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.document;
}

export async function updateCongregationDocument(
  documentId: number,
  payload: CongregationDocumentInput,
  signal?: AbortSignal,
): Promise<CongregationDocument> {
  const response = await apiFetch<{ document: CongregationDocument }>(
    buildBackendUrl(`/api/congregation/documents/${documentId}`),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.document;
}

export async function deleteCongregationDocument(
  documentId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/documents/${documentId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function verifyCongregationDocument(
  documentId: number,
  payload: CongregationDocumentVerification = { verified: true },
  signal?: AbortSignal,
): Promise<{ document: CongregationMemberDocument }> {
  return apiFetch<{ document: CongregationMemberDocument }>(buildBackendUrl(`/api/congregation/documents/${documentId}/verify`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function fetchCongregationPhotos(
  signal?: AbortSignal,
): Promise<CongregationPhoto[]> {
  try {
    const response = await apiFetch<CongregationPhotosResponse>(
      buildBackendUrl('/api/congregation/photos'),
      { signal },
    );
    return response.photos ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 404 || error.status === 403)) {
      return [];
    }
    throw error;
  }
}

export async function createCongregationPhoto(
  payload: CongregationPhotoInput,
  signal?: AbortSignal,
): Promise<CongregationPhoto> {
  const response = await apiFetch<{ photo: CongregationPhoto }>(
    buildBackendUrl('/api/congregation/photos'),
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  );
  return response.photo;
}

export async function deleteCongregationPhoto(
  photoId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<void>(buildBackendUrl(`/api/congregation/photos/${photoId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function setCongregationPhotoAsCover(
  photoId: number,
  signal?: AbortSignal,
): Promise<CongregationPhoto> {
  const response = await apiFetch<{ photo: CongregationPhoto }>(
    buildBackendUrl(`/api/congregation/photos/${photoId}/cover`),
    {
      method: 'PUT',
      signal,
    },
  );
  return response.photo;
}

export interface FetchCongregationHouseholdsParams {
  memberId?: number;
}

export async function fetchCongregationHouseholds(
  params?: FetchCongregationHouseholdsParams,
  signal?: AbortSignal,
): Promise<CongregationHousehold[]> {
  const searchParams = new URLSearchParams();
  if (typeof params?.memberId === 'number') {
    searchParams.set('memberId', String(params.memberId));
  }
  const query = searchParams.toString();
  const path = query
    ? `/api/congregation/households?${query}`
    : '/api/congregation/households';
  try {
    const response = await apiFetch<CongregationHouseholdsResponse>(
      buildBackendUrl(path),
      { signal },
    );
    return response.households ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 403 || error.status === 404)) {
      return [];
    }
    throw error;
  }
}

export async function fetchCongregationHouseholdDetail(
  householdId: number,
  signal?: AbortSignal,
): Promise<CongregationHousehold> {
  const response = await apiFetch<CongregationHouseholdResponse>(
    buildBackendUrl(`/api/congregation/households/${householdId}`),
    { signal },
  );
  return response.household;
}

export interface CongregationReportQueryParams {
  windowDays?: number;
  limit?: number;
}

const buildReportQuery = (params?: CongregationReportQueryParams): string => {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  if (typeof params.windowDays === 'number') {
    searchParams.set('windowDays', String(params.windowDays));
  }
  if (typeof params.limit === 'number') {
    searchParams.set('limit', String(params.limit));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export async function fetchCongregationBirthdayReport(
  params?: CongregationReportQueryParams,
  signal?: AbortSignal,
): Promise<CongregationBirthdayReportEntry[]> {
  const query = buildReportQuery(params);
  try {
    const response = await apiFetch<CongregationBirthdayReportResponse>(
      buildBackendUrl(`/api/congregation/reports/birthdays${query}`),
      { signal },
    );
    return response.birthdays ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 403 || error.status === 404)) {
      return [];
    }
    throw error;
  }
}

export async function fetchCongregationAnniversaryReport(
  params?: CongregationReportQueryParams,
  signal?: AbortSignal,
): Promise<CongregationAnniversaryReportEntry[]> {
  const query = buildReportQuery(params);
  try {
    const response = await apiFetch<CongregationAnniversaryReportResponse>(
      buildBackendUrl(`/api/congregation/reports/anniversaries${query}`),
      { signal },
    );
    return response.anniversaries ?? [];
  } catch (error) {
    if (isOldApiError(error) && (error.status === 403 || error.status === 404)) {
      return [];
    }
    throw error;
  }
}

/**
 * @deprecated Use donationService.createDonation() instead
 */
export async function createDonation(data: unknown) {
  return apiFetch(buildBackendUrl('/api/devotee/donation'), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * @deprecated Use subscriptionsService.verifyRazorpaySubscription() instead
 */
export async function verifyPayment(payload: unknown) {
  return apiFetch(buildBackendUrl('/api/public/payment/razorpay/verify'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * @deprecated Use userService.updateProfile() instead
 */
export async function updateUserProfile(data: unknown) {
  return apiFetch(buildBackendUrl('/api/user/me'), {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
export async function fetchUserRole() {
  return apiFetch(buildBackendUrl('/api/user/role'));
}

export async function searchDonations(query: string) {
  return apiFetch(
    buildBackendUrl(`/api/devotee/search/donation?q=${encodeURIComponent(query)}`),
  );
}

export async function downloadDonationReceipt(id: number | string) {
  const response = await fetch(buildBackendUrl(`/api/devotee/donation/receipt/${id}`), {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to download receipt');
  }
  return response.blob();
}

export async function uploadUserPhoto(file: File, onProgress?: (p: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildBackendUrl('/api/user/media/upload'));
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && data?.url) {
          resolve(data.url);
        } else {
          reject(new Error(data?.error || 'Upload failed'));
        }
      } catch (err) {
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(formData);
  });
}

export async function uploadFamilyMemberPhoto(file: File, onProgress?: (p: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildBackendUrl('/api/user/media/upload/family'));
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && data?.url) {
          resolve(data.url);
        } else {
          reject(new Error(data?.error || 'Upload failed'));
        }
      } catch (err) {
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(formData);
  });
}

export async function createCampaign(eventId: number) {
  return apiFetch(buildBackendUrl('/api/devotee/campaign'), {
    method: 'POST',
    body: JSON.stringify({ eventId }),
  });
}

/**
 * @deprecated Use `campaignsService.getCampaigns()` instead
 * @see src/api/services/campaigns.ts
 */
export async function fetchCampaigns() {
  return apiFetch(buildBackendUrl('/api/devotee/campaign'));
}

/**
 * @deprecated Use `subscriptionsService.getUserSubscriptions()` instead
 * @see src/api/services/subscriptions.ts
 */
export async function fetchUserSubscriptions() {
  return apiFetch(buildBackendUrl('/api/user/subscriptions'));
}

interface PlanList {
  items?: Record<string, unknown>[];
}

/**
 * @deprecated Use `subscriptionsService.getSubscriptionPlans()` instead
 * @see src/api/services/subscriptions.ts
 */
export async function fetchSubscriptionPlans(type = 'Nitya Seva') {
  const data = await apiFetch<PlanList | Record<string, unknown>[]>(
    buildBackendUrl(`/api/subscription/plans?type=${encodeURIComponent(type)}`),
  );
  if (Array.isArray(data)) return data;
  return data.items || [];
}

/**
 * @deprecated Use `subscriptionsService.createRazorpayPlan()` instead
 * @see src/api/services/subscriptions.ts
 */
export async function createRazorpayPlan(amount: number) {
  return apiFetch(buildBackendUrl('/api/razorpay/plan'), {
    method: 'POST',
    body: JSON.stringify({
      amount,
      interval: 'monthly',
      currency: 'INR',
      type: 'Nitya Seva',
      head: 'Nitya Seva',
    }),
  });
}

/**
 * @deprecated Use `subscriptionsService.createRazorpaySubscription()` instead
 * @see src/api/services/subscriptions.ts
 */
export async function createRazorpaySubscription(planId: string, userId: string) {
  return apiFetch(buildBackendUrl('/api/razorpay/subscription'), {
    method: 'POST',
    body: JSON.stringify({
      planId,
      customerNotify: 1,
      type: 'Nitya Seva',
      head: 'Nitya Seva',
      notes: { userId, head: 'Nitya Seva', purpose: 'Nitya Seva' },
    }),
  });
}

/**
 * @deprecated Use `subscriptionsService.verifyRazorpaySubscription()` instead
 * @see src/api/services/subscriptions.ts
 */
export async function verifyRazorpaySubscription(payload: {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}) {
  return apiFetch(buildBackendUrl('/api/razorpay/verify-subscription'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * @deprecated Use `subscriptionsService.cancelRazorpaySubscription()` instead
 * @see src/api/services/subscriptions.ts
 */
export async function cancelRazorpaySubscription(
  id: string,
  cancelAtCycleEnd = false,
) {
  return apiFetch(
    buildBackendUrl(`/api/razorpay/subscriptions/${id}/cancel`),
    {
      method: 'POST',
      body: JSON.stringify({ cancelAtCycleEnd }),
    },
  );
}

// Subscription donations history
export async function fetchSubscriptionDonations() {
  return apiFetch<Array<{ donation: Record<string, unknown>; subscription: Record<string, unknown> }>>(
    buildBackendUrl('/api/user/subscription/donations'),
  );
}

/**
 * @deprecated Use `leaderboardService.getUserMonthlyLeaderboard()` instead
 * @see src/api/services/leaderboard.ts
 */
export async function fetchLeaderboardUserMonthly() {
  return apiFetch(buildBackendUrl('/api/devotee/leaderboard/user/monthly'));
}

/**
 * @deprecated Use `leaderboardService.getUserQuarterlyLeaderboard()` instead
 * @see src/api/services/leaderboard.ts
 */
export async function fetchLeaderboardUserQuarterly() {
  return apiFetch(buildBackendUrl('/api/devotee/leaderboard/user/quarterly'));
}

/**
 * @deprecated Use `leaderboardService.getUserYearlyLeaderboard()` instead
 * @see src/api/services/leaderboard.ts
 */
export async function fetchLeaderboardUserYearly() {
  return apiFetch(buildBackendUrl('/api/devotee/leaderboard/user/yearly'));
}

/**
 * @deprecated Use `leaderboardService.getEventMonthlyLeaderboard()` instead
 * @see src/api/services/leaderboard.ts
 */
export async function fetchLeaderboardEventMonthly(eventId: number) {
  return apiFetch(buildBackendUrl(`/api/devotee/leaderboard/event/monthly/${eventId}`));
}

/**
 * @deprecated Use `leaderboardService.getEventQuarterlyLeaderboard()` instead
 * @see src/api/services/leaderboard.ts
 */
export async function fetchLeaderboardEventQuarterly(eventId: number) {
  return apiFetch(buildBackendUrl(`/api/devotee/leaderboard/event/quarterly/${eventId}`));
}

/**
 * @deprecated Use `leaderboardService.getEventYearlyLeaderboard()` instead
 * @see src/api/services/leaderboard.ts
 */
export async function fetchLeaderboardEventYearly(eventId: number) {
  return apiFetch(buildBackendUrl(`/api/devotee/leaderboard/event/yearly/${eventId}`));
}

/**
 * Mentor accept application
 */
export interface MentorReviewApplicationRequest {
  comment?: string;
}

export interface MentorReviewApplicationResponse {
  success: boolean;
  message?: string;
  application?: CongregationApplication;
}

export async function mentorAcceptApplication(
  applicationId: number,
  data: MentorReviewApplicationRequest,
  signal?: AbortSignal,
): Promise<MentorReviewApplicationResponse> {
  const response = await apiFetch<MentorReviewApplicationResponse>(
    buildBackendUrl(`/api/congregation/mentor/applications/${applicationId}/accept`),
    {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    },
  );
  return response;
}

export async function mentorRejectApplication(
  applicationId: number,
  data: MentorReviewApplicationRequest,
  signal?: AbortSignal,
): Promise<MentorReviewApplicationResponse> {
  const response = await apiFetch<MentorReviewApplicationResponse>(
    buildBackendUrl(`/api/congregation/mentor/applications/${applicationId}/reject`),
    {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    },
  );
  return response;
}

// ============================================================================
// BATCH MANAGEMENT API (Mentor endpoints - for mentor role)
// ============================================================================

export async function listCongregationBatches(signal?: AbortSignal): Promise<{ batches: CongregationBatch[] }> {
  return apiFetch(buildBackendUrl('/api/congregation/mentor/batches'), { signal });
}

// ============================================================================
// BATCH MANAGEMENT API (Admin endpoints - for admin role)
// ============================================================================

export async function listAdminCongregationBatches(signal?: AbortSignal): Promise<{ batches: CongregationBatch[] }> {
  return apiFetch(buildBackendUrl('/api/congregation/admin/batches'), { signal });
}

export async function getAdminCongregationBatch(id: number, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${id}`), { signal });
}

export async function createAdminCongregationBatch(payload: CongregationBatchInput, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl('/api/congregation/admin/batches'), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function updateAdminCongregationBatch(id: number, payload: CongregationBatchInput, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${id}`), {
    method: 'PUT',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function updateAdminCongregationBatchStatus(id: number, status: CongregationBatchStatus, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${id}/status`), {
    method: 'PUT',
    body: JSON.stringify({ status }),
    signal,
  });
}

export async function deleteAdminCongregationBatch(id: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${id}`), {
    method: 'DELETE',
    signal,
  });
}

export async function listAdminBatchEnrollments(batchId: number, signal?: AbortSignal): Promise<{ enrollments: CongregationBatchEnrollment[] }> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${batchId}/enrollments`), { signal });
}

export async function enrollAdminMemberInBatch(batchId: number, payload: CongregationBatchEnrollmentInput, signal?: AbortSignal): Promise<CongregationBatchEnrollment> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${batchId}/enrollments`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function updateAdminBatchEnrollmentStatus(batchId: number, enrollmentId: number, status: CongregationBatchEnrollmentStatus, signal?: AbortSignal): Promise<CongregationBatchEnrollment> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${batchId}/enrollments/${enrollmentId}/status`), {
    method: 'PUT',
    body: JSON.stringify({ status }),
    signal,
  });
}

export async function deleteAdminBatchEnrollment(batchId: number, enrollmentId: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${batchId}/enrollments/${enrollmentId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function listAdminBatchFacilitators(batchId: number, signal?: AbortSignal): Promise<{ facilitators: CongregationFacilitator[] }> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${batchId}/facilitators`), { signal });
}

export async function getCongregationBatch(id: number, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${id}`), { signal });
}

export async function createCongregationBatch(payload: CongregationBatchInput, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl('/api/congregation/mentor/batches'), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function updateCongregationBatch(id: number, payload: CongregationBatchInput, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${id}`), {
    method: 'PUT',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function updateCongregationBatchStatus(id: number, status: CongregationBatchStatus, signal?: AbortSignal): Promise<CongregationBatch> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${id}/status`), {
    method: 'PUT',
    body: JSON.stringify({ status }),
    signal,
  });
}

export async function deleteCongregationBatch(id: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${id}`), {
    method: 'DELETE',
    signal,
  });
}

export async function listBatchEnrollments(batchId: number, signal?: AbortSignal): Promise<{ enrollments: CongregationBatchEnrollment[] }> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/enrollments`), { signal });
}

export async function enrollMemberInBatch(batchId: number, payload: CongregationBatchEnrollmentInput, signal?: AbortSignal): Promise<CongregationBatchEnrollment> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/enrollments`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function listMentorAssignedDevotees() {
  const response = await apiFetch<{ devotees: any[] }>(
    buildBackendUrl('/api/congregation/mentor/sadhana/devotees'),
  );
  // Map backend DevoteeWithStatus to CongregationMember-like structure
  return {
    devotees: (response.devotees || []).map((devotee: any) => ({
      id: devotee.memberId, // Use memberId, not id (id is userId)
      displayName: devotee.name || devotee.displayName,
      email: devotee.email || '',
      createdAt: new Date().toISOString(), // Dummy value for required field
    })) as CongregationMember[],
  };
}

export async function updateBatchEnrollmentStatus(
  batchId: number,
  enrollmentId: number,
  status: CongregationBatchEnrollmentStatus,
  signal?: AbortSignal,
): Promise<CongregationBatchEnrollment> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/enrollments/${enrollmentId}/status`), {
    method: 'PUT',
    body: JSON.stringify({ status }),
    signal,
  });
}

export async function deleteBatchEnrollment(batchId: number, enrollmentId: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/enrollments/${enrollmentId}`), {
    method: 'DELETE',
    signal,
  });
}

// ============================================================================
// BATCH FACILITATOR API (Mentor can assign facilitators)
// ============================================================================

export async function listBatchFacilitators(batchId: number, signal?: AbortSignal): Promise<{ facilitators: CongregationFacilitator[] }> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/facilitators`), { signal });
}

export async function assignBatchFacilitator(payload: { memberId: number; batchId: number; bio?: string }, signal?: AbortSignal): Promise<CongregationFacilitator> {
  return apiFetch(buildBackendUrl('/api/congregation/mentor/facilitators'), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function removeBatchFacilitator(batchId: number, memberId: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/facilitators/${memberId}`), {
    method: 'DELETE',
    signal,
  });
}

// ============================================================================
// BATCH POSTS API (Mentor & Devotee Messaging System)
// ============================================================================

export async function listBatchPosts(batchId: number, memberId?: number, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails[]> {
  const url = memberId 
    ? buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/posts?memberId=${memberId}`)
    : buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/posts`);
  return apiFetch(url, { signal });
}

export async function createBatchPost(batchId: number, payload: CongregationBatchPostInput, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/posts`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function getBatchPost(postId: number, memberId?: number, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  const url = memberId
    ? buildBackendUrl(`/api/congregation/mentor/posts/${postId}?memberId=${memberId}`)
    : buildBackendUrl(`/api/congregation/mentor/posts/${postId}`);
  return apiFetch(url, { signal });
}

export async function updateBatchPost(postId: number, content: string, isPinned?: boolean, attachments?: any[], signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/posts/${postId}`), {
    method: 'PUT',
    body: JSON.stringify({ content, isPinned, attachments }),
    signal,
  });
}

export async function deleteBatchPost(postId: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/posts/${postId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function addPostReaction(postId: number, reactionType: BatchPostReactionType, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/posts/${postId}/reactions`), {
    method: 'POST',
    body: JSON.stringify({ reactionType }),
    signal,
  });
}

export async function removePostReaction(postId: number, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/posts/${postId}/reactions`), {
    method: 'DELETE',
    signal,
  });
}

export async function listPostReplies(postId: number, signal?: AbortSignal): Promise<CongregationBatchPostReply[]> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/posts/${postId}/replies`), { signal });
}

export async function createPostReply(postId: number, payload: CongregationBatchPostReplyInput, signal?: AbortSignal): Promise<CongregationBatchPostReply> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/posts/${postId}/replies`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function deletePostReply(replyId: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/replies/${replyId}`), {
    method: 'DELETE',
    signal,
  });
}

// --- Admin batch posts (mirrors mentor functions but uses /congregation/admin/ routes) ---

export async function adminListBatchPosts(batchId: number, memberId?: number, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails[]> {
  const query = memberId ? `?memberId=${memberId}` : '';
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${batchId}/posts${query}`), { signal });
}

export async function adminCreateBatchPost(batchId: number, payload: CongregationBatchPostInput, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/batches/${batchId}/posts`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function adminUpdateBatchPost(postId: number, content: string, isPinned?: boolean, attachments?: any[], signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/posts/${postId}`), {
    method: 'PUT',
    body: JSON.stringify({ content, isPinned, attachments }),
    signal,
  });
}

export async function adminDeleteBatchPost(postId: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/posts/${postId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function adminAddPostReaction(postId: number, reactionType: BatchPostReactionType, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/posts/${postId}/reactions`), {
    method: 'POST',
    body: JSON.stringify({ reactionType }),
    signal,
  });
}

export async function adminRemovePostReaction(postId: number, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/posts/${postId}/reactions`), {
    method: 'DELETE',
    signal,
  });
}

export async function adminListPostReplies(postId: number, signal?: AbortSignal): Promise<CongregationBatchPostReply[]> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/posts/${postId}/replies`), { signal });
}

export async function adminCreatePostReply(postId: number, payload: CongregationBatchPostReplyInput, signal?: AbortSignal): Promise<CongregationBatchPostReply> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/posts/${postId}/replies`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function adminDeletePostReply(replyId: number, signal?: AbortSignal): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/posts/replies/${replyId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function votePoll(postId: number, optionId: number, signal?: AbortSignal): Promise<CongregationBatchPostWithDetails> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/posts/${postId}/poll-votes`), {
    method: 'POST',
    body: JSON.stringify({ optionId }),
    signal,
  });
}

export async function listDevoteeBatches(signal?: AbortSignal): Promise<{ batches: CongregationBatch[] }> {
  return apiFetch(buildBackendUrl('/api/congregation/devotee/batches'), { signal });
}

// ============================================================================
// Batch Attendance Functions
// ============================================================================

export async function createBatchAttendance(
  batchId: number,
  payload: BatchAttendanceInput,
  signal?: AbortSignal
): Promise<BatchAttendanceWithRecords> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/attendance`), {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

export async function listBatchAttendance(
  batchId: number,
  startDate?: string,
  endDate?: string,
  signal?: AbortSignal
): Promise<{ attendances: BatchAttendance[] }> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/attendance${query}`), { signal });
}

export async function getBatchAttendanceById(
  batchId: number,
  attendanceId: number,
  signal?: AbortSignal
): Promise<BatchAttendanceWithRecords> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/attendance/${attendanceId}`), { signal });
}

export async function deleteBatchAttendance(
  batchId: number,
  attendanceId: number,
  signal?: AbortSignal
): Promise<void> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/attendance/${attendanceId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function listAllBatchAttendance(
  startDate?: string,
  endDate?: string,
  batchId?: number,
  mentorId?: number,
  signal?: AbortSignal
): Promise<{ attendances: BatchAttendance[] }> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (batchId) params.append('batchId', batchId.toString());
  if (mentorId) params.append('mentorId', mentorId.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(buildBackendUrl(`/api/congregation/admin/attendance${query}`), { signal });
}

export async function getMemberAttendanceHistory(
  memberId: number,
  startDate?: string,
  endDate?: string,
  signal?: AbortSignal
): Promise<{ records: BatchAttendanceRecord[] }> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(buildBackendUrl(`/api/congregation/members/${memberId}/attendance${query}`), { signal });
}

// Services
export async function getServiceTypes(
  signal?: AbortSignal
): Promise<{ serviceTypes: CongregationServiceType[] }> {
  return apiFetch(buildBackendUrl('/api/congregation/services/types'), { signal });
}

export async function getMyServices(
  signal?: AbortSignal
): Promise<{ services: CongregationService[] }> {
  return apiFetch(buildBackendUrl('/api/congregation/my/services'), { signal });
}

export async function getService(
  serviceId: number,
  signal?: AbortSignal
): Promise<CongregationService> {
  return apiFetch(buildBackendUrl(`/api/congregation/services/${serviceId}`), { signal });
}

export async function createService(
  input: CongregationServiceInput,
  signal?: AbortSignal
): Promise<CongregationService> {
  return apiFetch(buildBackendUrl('/api/congregation/mentor/services'), {
    method: 'POST',
    body: JSON.stringify(input),
    signal,
  });
}

export async function updateService(
  serviceId: number,
  input: CongregationServiceInput,
  signal?: AbortSignal
): Promise<CongregationService> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/services/${serviceId}`), {
    method: 'PUT',
    body: JSON.stringify(input),
    signal,
  });
}

export async function listBatchServices(
  batchId: number,
  status?: string,
  signal?: AbortSignal
): Promise<{ services: CongregationService[] }> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/batches/${batchId}/services${query}`), { signal });
}

export async function deleteService(
  serviceId: number,
  signal?: AbortSignal
): Promise<{ message: string }> {
  return apiFetch(buildBackendUrl(`/api/congregation/mentor/services/${serviceId}`), {
    method: 'DELETE',
    signal,
  });
}

export async function createAdminService(
  input: CongregationServiceInput,
  signal?: AbortSignal
): Promise<CongregationService> {
  return apiFetch(buildBackendUrl('/api/congregation/admin/services'), {
    method: 'POST',
    body: JSON.stringify(input),
    signal,
  });
}

export async function updateAdminService(
  serviceId: number,
  input: CongregationServiceInput,
  signal?: AbortSignal
): Promise<CongregationService> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/services/${serviceId}`), {
    method: 'PUT',
    body: JSON.stringify(input),
    signal,
  });
}

export async function deleteAdminService(
  serviceId: number,
  signal?: AbortSignal
): Promise<{ message: string }> {
  return apiFetch(buildBackendUrl(`/api/congregation/admin/services/${serviceId}`), {
    method: 'DELETE',
    signal,
  });
}


// =============================================================================
// Congregation Admin API (ported from admin.iskconkanpur.org)
// =============================================================================

// --- Access check ---

export async function checkCongregationAccess() {
  return apiFetch<{
    canAccess: boolean;
    isAdmin: boolean;
    isFacilitator: boolean;
    facilitatorBatches: number[];
    member: import('../types/congregation').CongregationMember;
    roles: import('../types/congregation').CongregationRoleAssignment[];
  }>(buildBackendUrl('/api/congregation/access-check'));
}

// --- Admin filter / applications ---

export interface CongregationAdminFilter {
  centerId?: number | string;
  status?: import('../types/congregation').CongregationApplicationStatus;
}

function _buildCongregationAdminQuery(params?: CongregationAdminFilter) {
  const searchParams = new URLSearchParams();
  if (params?.centerId) searchParams.set('centerId', String(params.centerId));
  if (params?.status) searchParams.set('status', params.status);
  const query = searchParams.toString();
  return query ? '?' + query : '';
}

export async function fetchCongregationAdminApplications(params?: CongregationAdminFilter) {
  const query = _buildCongregationAdminQuery(params);
  return apiFetch<import('../types/congregation').CongregationAdminApplicationsResponse>(
    buildBackendUrl('/api/congregation/admin/applications' + query),
  );
}

export async function fetchCongregationAdminPipeline(params?: CongregationAdminFilter) {
  const query = _buildCongregationAdminQuery(params);
  return apiFetch<import('../types/congregation').CongregationAdminPipelineResponse>(
    buildBackendUrl('/api/congregation/admin/reports/applications/pipeline' + query),
  );
}

export interface CongregationAdminActivityFilter {
  limit?: number;
  cursor?: string | null;
}

export async function fetchCongregationAdminActivity(params?: CongregationAdminActivityFilter) {
  const searchParams = new URLSearchParams();
  if (typeof params?.limit === 'number') searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  const query = searchParams.toString();
  return apiFetch<import('../types/congregation').CongregationAdminActivityResponse>(
    buildBackendUrl('/api/congregation/admin/activity' + (query ? '?' + query : '')),
  );
}

export async function fetchCongregationApplication(id: number) {
  const response = await apiFetch<any>(buildBackendUrl('/api/congregation/applications/' + id));
  if (response?.events) {
    response.events = response.events.map((event: any) => ({
      ...event,
      type: event.eventType || event.type,
      actorName: event.actorName || null,
    }));
  }
  return response as import('../types/congregation').CongregationApplicationDetail;
}

export async function assignCongregationApplication(id: number, payload: import('../types/congregation').CongregationAdminAssignInput) {
  return apiFetch<{ application: import('../types/congregation').CongregationApplication }>(
    buildBackendUrl('/api/congregation/admin/applications/' + id + '/assign'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function overrideCongregationApplication(id: number, payload: import('../types/congregation').CongregationAdminOverrideInput) {
  return apiFetch<{ application: import('../types/congregation').CongregationApplication }>(
    buildBackendUrl('/api/congregation/admin/applications/' + id + '/override'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

// --- Masters CRUD ---

export async function createCongregationCenter(payload: import('../types/congregation').CongregationCenterInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/centers'), { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCongregationCenter(id: number, payload: import('../types/congregation').CongregationCenterInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/centers/' + id), { method: 'PUT', body: JSON.stringify(payload) });
}

export async function createCongregationCourse(payload: import('../types/congregation').CongregationCourseInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/courses'), { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCongregationCourse(id: number, payload: import('../types/congregation').CongregationCourseInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/courses/' + id), { method: 'PUT', body: JSON.stringify(payload) });
}

export async function createCongregationBook(payload: import('../types/congregation').CongregationBookInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/books'), { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCongregationBook(id: number, payload: import('../types/congregation').CongregationBookInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/books/' + id), { method: 'PUT', body: JSON.stringify(payload) });
}

export async function createCongregationSkill(payload: import('../types/congregation').CongregationSkillInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/skills'), { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCongregationSkill(id: number, payload: import('../types/congregation').CongregationSkillInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/skills/' + id), { method: 'PUT', body: JSON.stringify(payload) });
}

export async function createCongregationSpiritualMaster(payload: import('../types/congregation').CongregationSpiritualMasterInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/spiritual-masters'), { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCongregationSpiritualMaster(id: number, payload: import('../types/congregation').CongregationSpiritualMasterInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/spiritual-masters/' + id), { method: 'PUT', body: JSON.stringify(payload) });
}

export async function createCongregationSpiritualGuide(payload: import('../types/congregation').CongregationSpiritualGuideInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/spiritual-guides'), { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCongregationSpiritualGuide(id: number, payload: import('../types/congregation').CongregationSpiritualGuideInput) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/spiritual-guides/' + id), { method: 'PUT', body: JSON.stringify(payload) });
}

// --- Member admin ---

export interface AdminUser {
  id: number;
  email?: string | null;
  phone?: string | null;
  displayName?: string | null;
  legalName?: string | null;
  photoUrl?: string | null;
  role?: string | null;
  status?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export async function fetchAdminUsers(query = '', limit?: number) {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return apiFetch<{ users: AdminUser[] }>(buildBackendUrl('/api/admin/users' + (qs ? '?' + qs : '')));
}

export async function lookupCongregationMemberByUserId(userId: number) {
  return apiFetch<{ memberId: number; member: import('../types/congregation').CongregationMemberProfile }>(
    buildBackendUrl('/api/congregation/admin/members/lookup'),
    { method: 'POST', body: JSON.stringify({ userId }) },
  );
}

export async function assignCongregationRole(payload: import('../types/congregation').CongregationRoleAssignmentInput) {
  return apiFetch<{ role: import('../types/congregation').CongregationRoleAssignment }>(
    buildBackendUrl('/api/congregation/admin/roles'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function revokeCongregationRole(id: number) {
  return apiFetch(buildBackendUrl('/api/congregation/admin/roles/' + id), { method: 'DELETE' });
}

export async function createCongregationMemberFromUser(userId: number) {
  return apiFetch<{ member: import('../types/congregation').CongregationMemberProfile }>(
    buildBackendUrl('/api/congregation/admin/members/from-user'),
    { method: 'POST', body: JSON.stringify({ userId }) },
  );
}

export async function createCongregationMemberWithProfile(payload: {
  userId: number; displayName: string; legalName: string; phone: string; email: string;
  occupation: string; city: string; state: string; country: string; gender: string;
  maritalStatus: string; spiritualMasterId?: number | null; whatsappOptIn: boolean;
}) {
  return apiFetch<{ member: import('../types/congregation').CongregationMember }>(
    buildBackendUrl('/api/congregation/admin/members/with-profile'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function searchCongregationMembers(query: string, options: { centerId?: string | number; limit?: number } = {}) {
  const params = new URLSearchParams({ q: query });
  if (options.centerId) params.set('centerId', String(options.centerId));
  if (options.limit) params.set('limit', String(options.limit));
  return apiFetch<{ results: import('../types/congregation').CongregationMemberSearchResult[] }>(
    buildBackendUrl('/api/congregation/admin/members/search?' + params.toString()),
  );
}

export async function fetchAdminMembersWithActivityStatus() {
  const response = await apiFetch<{ devotees: Array<any>; total: number }>(buildBackendUrl('/api/congregation/admin/members/activity'));
  return {
    devotees: (response.devotees || []).map((r: any) => ({
      id: r.id,
      memberId: r.memberId,
      name: r.name || (r.id ? 'Member #' + r.id : 'Member'),
      email: r.email || undefined,
      status: r.status || 'inactive',
      membershipStatus: r.membershipStatus,
      lastSubmissionAt: r.lastSubmissionAt || null,
    })),
  };
}

export async function fetchCongregationMemberProfile(memberId?: number) {
  const params = new URLSearchParams();
  if (memberId) params.set('memberId', String(memberId));
  const query = params.toString();
  return apiFetch<import('../types/congregation').CongregationMemberProfile>(
    buildBackendUrl('/api/congregation/profile' + (query ? '?' + query : '')),
  );
}

export async function updateAdminMemberPersonal(memberId: number, payload: any) {
  return apiFetch<{ member: import('../types/congregation').CongregationMember }>(
    buildBackendUrl('/api/congregation/admin/members/personal?memberId=' + memberId),
    { method: 'PUT', body: JSON.stringify(payload) },
  );
}

// --- Reports ---

export interface CongregationReportFilter {
  windowDays?: number;
  limit?: number;
  centerId?: number | string;
}

function _buildCongRegReportQuery(params?: CongregationReportFilter) {
  const search = new URLSearchParams();
  if (params?.windowDays !== undefined) search.set('windowDays', String(params.windowDays));
  if (params?.limit !== undefined) search.set('limit', String(params.limit));
  if (params?.centerId !== undefined) search.set('centerId', String(params.centerId));
  const query = search.toString();
  return query ? '?' + query : '';
}

export async function fetchCongregationBirthdays(params?: CongregationReportFilter) {
  return apiFetch<{ birthdays: import('../types/congregation').CongregationBirthdayReport[] }>(
    buildBackendUrl('/api/congregation/reports/birthdays' + _buildCongRegReportQuery(params)),
  );
}

export async function fetchCongregationAnniversaries(params?: CongregationReportFilter) {
  return apiFetch<{ anniversaries: import('../types/congregation').CongregationAnniversaryReport[] }>(
    buildBackendUrl('/api/congregation/reports/anniversaries' + _buildCongRegReportQuery(params)),
  );
}

// --- Household admin ---

export async function createCongregationHousehold(payload: import('../types/congregation').CongregationHouseholdInput) {
  return apiFetch<{ household: import('../types/congregation').CongregationHousehold }>(
    buildBackendUrl('/api/congregation/admin/households'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function updateCongregationHousehold(id: number, payload: Partial<import('../types/congregation').CongregationHouseholdInput>) {
  return apiFetch<{ household: import('../types/congregation').CongregationHousehold }>(
    buildBackendUrl('/api/congregation/admin/households/' + id),
    { method: 'PUT', body: JSON.stringify(payload) },
  );
}

export async function addCongregationHouseholdMember(householdId: number, payload: import('../types/congregation').CongregationHouseholdMemberInput) {
  return apiFetch<{ member: import('../types/congregation').CongregationHouseholdMember }>(
    buildBackendUrl('/api/congregation/admin/households/' + householdId + '/members'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function updateCongregationHouseholdMember(memberId: number, payload: import('../types/congregation').CongregationHouseholdMemberUpdateInput) {
  return apiFetch<{ member: import('../types/congregation').CongregationHouseholdMember }>(
    buildBackendUrl('/api/congregation/admin/household-members/' + memberId),
    { method: 'PUT', body: JSON.stringify(payload) },
  );
}

export interface CongregationHouseholdMemberRemovalOptions { justification?: string; }

export async function removeCongregationHouseholdMember(memberId: number, options: CongregationHouseholdMemberRemovalOptions = {}) {
  const search = new URLSearchParams();
  if (options.justification) search.set('justification', options.justification);
  const query = search.toString();
  return apiFetch(
    buildBackendUrl('/api/congregation/admin/household-members/' + memberId + (query ? '?' + query : '')),
    { method: 'DELETE' },
  );
}

export async function setCongregationHouseholdHead(householdId: number, payload: import('../types/congregation').CongregationHouseholdHeadInput) {
  return apiFetch<{ household: import('../types/congregation').CongregationHousehold }>(
    buildBackendUrl('/api/congregation/admin/households/' + householdId + '/head'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function setCongregationHouseholdLabels(householdId: number, payload: import('../types/congregation').CongregationHouseholdLabelAssignmentInput) {
  return apiFetch<{ household: import('../types/congregation').CongregationHousehold }>(
    buildBackendUrl('/api/congregation/admin/households/' + householdId + '/labels'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function fetchCongregationHouseholdLabels() {
  return apiFetch<{ labels: import('../types/congregation').CongregationHouseholdLabel[] }>(
    buildBackendUrl('/api/congregation/admin/household-labels'),
  );
}

export async function createCongregationHouseholdLabel(payload: import('../types/congregation').CongregationHouseholdLabelInput) {
  return apiFetch<{ label: import('../types/congregation').CongregationHouseholdLabel }>(
    buildBackendUrl('/api/congregation/admin/household-labels'),
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function updateCongregationHouseholdLabel(id: number, payload: Partial<import('../types/congregation').CongregationHouseholdLabelInput>) {
  return apiFetch<{ label: import('../types/congregation').CongregationHouseholdLabel }>(
    buildBackendUrl('/api/congregation/admin/household-labels/' + id),
    { method: 'PUT', body: JSON.stringify(payload) },
  );
}

export interface CongregationHouseholdRosterFilter {
  centerId?: number | string; search?: string;
  labelIds?: Array<number | string>; page?: number; pageSize?: number;
}

export async function fetchCongregationHouseholdRoster(params: CongregationHouseholdRosterFilter = {}) {
  const search = new URLSearchParams();
  if (params.centerId !== undefined) search.set('centerId', String(params.centerId));
  if (params.search) search.set('search', params.search);
  if (params.labelIds?.length) search.set('labelIds', params.labelIds.map(String).join(','));
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return apiFetch<{ households: import('../types/congregation').CongregationHouseholdRosterEntry[] }>(
    buildBackendUrl('/api/congregation/admin/households/roster' + (query ? '?' + query : '')),
  );
}

export async function exportCongregationHouseholdRoster(params: CongregationHouseholdRosterFilter = {}) {
  const search = new URLSearchParams();
  if (params.centerId !== undefined && params.centerId !== '') search.set('centerId', String(params.centerId));
  if (params.search) search.set('search', params.search);
  if (params.labelIds?.length) search.set('labelIds', params.labelIds.map(String).join(','));
  const query = search.toString();
  const response = await fetch(
    buildBackendUrl('/api/congregation/admin/households/export' + (query ? '?' + query : '')),
    { credentials: 'include' },
  );
  if (!response.ok) {
    let message = 'Unable to export households';
    try {
      const raw = await response.text();
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object' && 'message' in parsed) {
            message = String((parsed as { message?: unknown }).message ?? message);
          }
        } catch { message = raw; }
      }
    } catch { /* ignore */ }
    throw new ApiError(message, response.status);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  let filename = 'congregation-households_' + ts + '.csv';
  if (disposition) {
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    if (match?.[1]) filename = match[1];
  }
  return { blob, filename };
}

// --- Eligibility ---

export async function fetchCongregationEligibilitySummary() {
  return apiFetch<{ rules: import('../types/congregation').CongregationEligibilitySummary[] }>(buildBackendUrl('/api/congregation/admin/reports/eligibility'));
}
export async function fetchCongregationMentorCohorts() {
  return apiFetch<{ cohorts: import('../types/congregation').CongregationMentorCohortSummary[] }>(buildBackendUrl('/api/congregation/admin/reports/mentor-cohorts'));
}
export async function fetchCongregationChantingProgress() {
  return apiFetch<{ buckets: import('../types/congregation').CongregationChantingProgressRow[] }>(buildBackendUrl('/api/congregation/admin/reports/chanting-progress'));
}
export async function fetchCongregationCourseHeatmap() {
  return apiFetch<{ courses: import('../types/congregation').CongregationCourseHeatmapRow[] }>(buildBackendUrl('/api/congregation/admin/reports/course-heatmap'));
}
export async function fetchCongregationEligibilityRules() {
  return apiFetch<{ rules: import('../types/congregation').CongregationEligibilityRule[] }>(buildBackendUrl('/api/congregation/admin/eligibility/rules'));
}
export async function createCongregationEligibilityRule(payload: import('../types/congregation').CongregationEligibilityRuleInput) {
  return apiFetch<{ rule: import('../types/congregation').CongregationEligibilityRule }>(buildBackendUrl('/api/congregation/admin/eligibility/rules'), { method: 'POST', body: JSON.stringify(payload) });
}
export async function updateCongregationEligibilityRule(id: number, payload: import('../types/congregation').CongregationEligibilityRuleInput) {
  return apiFetch<{ rule: import('../types/congregation').CongregationEligibilityRule }>(buildBackendUrl('/api/congregation/admin/eligibility/rules/' + id), { method: 'PUT', body: JSON.stringify(payload) });
}
export async function evaluateCongregationEligibilityRule(ruleId: number, memberId: number) {
  return apiFetch<{ evaluation: import('../types/congregation').CongregationEligibilityEvaluation }>(buildBackendUrl('/api/congregation/admin/eligibility/rules/' + ruleId + '/evaluate?memberId=' + memberId), { method: 'POST' });
}

// --- Imports & Backups ---

export async function fetchCongregationImports() {
  return apiFetch<{ imports: import('../types/congregation').CongregationImportJob[] }>(buildBackendUrl('/api/congregation/admin/imports'));
}
export async function createCongregationImport(payload: import('../types/congregation').CongregationImportRequest) {
  return apiFetch<{ import: import('../types/congregation').CongregationImportJob }>(buildBackendUrl('/api/congregation/admin/imports'), { method: 'POST', body: JSON.stringify(payload) });
}
export async function fetchCongregationImportRows(importId: number) {
  return apiFetch<{ rows: import('../types/congregation').CongregationImportJobRow[] }>(buildBackendUrl('/api/congregation/admin/imports/' + importId + '/rows'));
}
export async function fetchCongregationBackups() {
  return apiFetch<{ backups: import('../types/congregation').CongregationBackupRun[] }>(buildBackendUrl('/api/congregation/admin/backups'));
}
export async function createCongregationBackup(payload: import('../types/congregation').CongregationBackupRequest) {
  return apiFetch<{ backup: import('../types/congregation').CongregationBackupRun }>(buildBackendUrl('/api/congregation/admin/backups'), { method: 'POST', body: JSON.stringify(payload) });
}
export async function updateCongregationBackup(backupId: number, payload: import('../types/congregation').CongregationBackupUpdate) {
  return apiFetch<{ backup: import('../types/congregation').CongregationBackupRun }>(buildBackendUrl('/api/congregation/admin/backups/' + backupId), { method: 'PATCH', body: JSON.stringify(payload) });
}

// --- Service type admin ---

export async function createServiceType(name: string, description: string) {
  return apiFetch<import('../types/congregation').CongregationServiceType>(buildBackendUrl('/api/congregation/admin/services/types'), { method: 'POST', body: JSON.stringify({ name, description }) });
}
export async function updateServiceType(id: number, name: string, description: string) {
  return apiFetch<import('../types/congregation').CongregationServiceType>(buildBackendUrl('/api/congregation/admin/services/types/' + id), { method: 'PUT', body: JSON.stringify({ name, description }) });
}

// --- Book reading admin ---

export async function listAllBookReadings(): Promise<import('../types/congregation').CongregationBookReading[]> {
  return apiFetch<import('../types/congregation').CongregationBookReading[]>(buildBackendUrl('/api/congregation/admin/all-book-readings'));
}
export async function listAllBookRecommendations(): Promise<import('../types/congregation').CongregationBookRecommendation[]> {
  return apiFetch<import('../types/congregation').CongregationBookRecommendation[]>(buildBackendUrl('/api/congregation/admin/all-book-recommendations'));
}
export async function deleteBookRecommendation(id: number): Promise<void> {
  return apiFetch(buildBackendUrl('/api/congregation/admin/book-recommendations/' + id), { method: 'DELETE' });
}

// --- Legacy sadhana admin (ported congregation pages use admin-portal function names) ---

const _sadminQ = (params: Record<string, unknown>) => {
  const s = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) s.set(k, String(v)); });
  const q = s.toString();
  return q ? '?' + q : '';
};

export async function fetchSadhanaCurrentAssignment(options: { memberId?: number } = {}): Promise<import('../types/sadhana').SadhanaCurrentAssignmentResponse> {
  return apiFetch<import('../types/sadhana').SadhanaCurrentAssignmentResponse>(buildBackendUrl('/api/congregation/sadhana/templates/assignment' + _sadminQ({ memberId: options.memberId })));
}
export async function fetchSadhanaAssignments(options: { memberId?: number } = {}): Promise<import('../types/sadhana').SadhanaAssignmentsResponse> {
  return apiFetch<import('../types/sadhana').SadhanaAssignmentsResponse>(buildBackendUrl('/api/congregation/sadhana/templates/assignments' + _sadminQ({ memberId: options.memberId })));
}
export async function createSadhanaAssignment(payload: import('../types/sadhana').SadhanaTemplateAssignmentRequest, options: { memberId?: number } = {}): Promise<import('../types/sadhana').SadhanaAssignmentResponse> {
  return apiFetch<import('../types/sadhana').SadhanaAssignmentResponse>(buildBackendUrl('/api/congregation/sadhana/templates/assignments' + _sadminQ({ memberId: options.memberId })), { method: 'POST', body: JSON.stringify(payload) });
}
export async function fetchSadhanaDefaultTemplate(): Promise<import('../types/sadhana').SadhanaTemplateResponse> {
  return apiFetch<import('../types/sadhana').SadhanaTemplateResponse>(buildBackendUrl('/api/congregation/sadhana/templates/default'));
}
export async function fetchSadhanaWeek(weekId: number, options: { memberId?: number } = {}): Promise<import('../types/sadhana').SadhanaWeekDetailResponse> {
  return apiFetch<import('../types/sadhana').SadhanaWeekDetailResponse>(buildBackendUrl('/api/congregation/sadhana/weeks/' + weekId + _sadminQ({ memberId: options.memberId })));
}
export async function submitSadhanaWeek(weekId: number, options: { memberId?: number } = {}): Promise<import('../types/sadhana').SadhanaWeekSubmitResponse> {
  return apiFetch<import('../types/sadhana').SadhanaWeekSubmitResponse>(buildBackendUrl('/api/congregation/sadhana/weeks/' + weekId + '/submit' + _sadminQ({ memberId: options.memberId })), { method: 'PUT' });
}
export async function updateSadhanaWeekMentorComment(weekId: number, payload: import('../types/sadhana').SadhanaWeekMentorCommentRequest, options: { memberId?: number } = {}): Promise<import('../types/sadhana').SadhanaWeekMentorCommentResponse> {
  return apiFetch<import('../types/sadhana').SadhanaWeekMentorCommentResponse>(buildBackendUrl('/api/congregation/sadhana/weeks/' + weekId + '/mentor-comment' + _sadminQ({ memberId: options.memberId })), { method: 'PUT', body: JSON.stringify(payload) });
}
export async function fetchSadhanaMigrationStatus(options: { memberId?: number } = {}): Promise<import('../types/sadhana').SadhanaMigrationStatusResponse> {
  return apiFetch<import('../types/sadhana').SadhanaMigrationStatusResponse>(buildBackendUrl('/api/congregation/sadhana/migration/status' + _sadminQ({ memberId: options.memberId })));
}
export async function fetchSadhanaMigrationRuns(options: { memberId?: number; limit?: number; before?: string | null } = {}): Promise<import('../types/sadhana').SadhanaMigrationRunListResponse> {
  return apiFetch<import('../types/sadhana').SadhanaMigrationRunListResponse>(buildBackendUrl('/api/congregation/sadhana/migration/runs' + _sadminQ({ memberId: options.memberId, limit: options.limit, before: options.before })));
}
export async function triggerSadhanaReseed(options: { memberId: number }, payload: import('../types/sadhana').SadhanaReseedRequest): Promise<import('../types/sadhana').SadhanaReseedResponse> {
  return apiFetch<import('../types/sadhana').SadhanaReseedResponse>(buildBackendUrl('/api/congregation/sadhana/migration/reseed' + _sadminQ({ memberId: options.memberId })), { method: 'POST', body: JSON.stringify(payload) });
}
export async function fetchChantingPrescriptions(options: { memberId?: number } = {}): Promise<import('../types/sadhana').ChantingPrescriptionListResponse> {
  return apiFetch<import('../types/sadhana').ChantingPrescriptionListResponse>(buildBackendUrl('/api/congregation/chanting' + _sadminQ({ memberId: options.memberId })));
}
export async function createChantingPrescription(payload: import('../types/sadhana').ChantingPrescriptionCreateRequest, options: { memberId?: number } = {}): Promise<import('../types/sadhana').ChantingPrescriptionCreateResponse> {
  return apiFetch<import('../types/sadhana').ChantingPrescriptionCreateResponse>(buildBackendUrl('/api/congregation/chanting' + _sadminQ({ memberId: options.memberId })), { method: 'POST', body: JSON.stringify(payload) });
}
export async function fetchAdminDevoteeSadhanaCard(memberId: number, weekStart?: string): Promise<any> {
  const params = new URLSearchParams();
  params.append('memberId', String(memberId));
  if (weekStart) params.append('weekStartDate', weekStart);
  return apiFetch<any>(buildBackendUrl('/api/congregation/admin/sadhana/devotee/card?' + params.toString()));
}
export async function fetchAdminDevoteeSadhanaCards(memberId: number, limit = 10, offset = 0): Promise<import('../types/sadhana').SadhanaWeekListResponse> {
  const params = new URLSearchParams();
  params.append('memberId', String(memberId));
  params.append('limit', Math.min(Math.max(limit, 1), 52).toString());
  return apiFetch<import('../types/sadhana').SadhanaWeekListResponse>(buildBackendUrl('/api/congregation/admin/sadhana/devotee/cards?' + params.toString()));
}

export interface AdminMemberWithActivity {
  id: number;
  memberId: number;
  name: string;
  email: string;
  phoneNumber: string;
  status: 'active' | 'inactive' | string;
  membershipStatus: string;
  lastSubmissionAt: string | null;
}
export async function listAdminMembersWithActivity(): Promise<{ devotees: AdminMemberWithActivity[]; total: number }> {
  return apiFetch<{ devotees: AdminMemberWithActivity[]; total: number }>(
    buildBackendUrl('/api/congregation/admin/members/activity'),
  );
}

export async function fetchAdminWeekSadhanaCards(weekStartDate?: string): Promise<{ cards: any[] }> {
  const params = new URLSearchParams();
  if (weekStartDate) params.append('weekStartDate', weekStartDate);
  const query = params.toString();
  return apiFetch<{ cards: any[] }>(
    buildBackendUrl('/api/congregation/admin/sadhana/week' + (query ? '?' + query : '')),
  );
}
